import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Chip,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Card,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Link,
  Autocomplete,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";

import { CHANNEL_CARDS, CHANNEL_TYPES, findCardById } from "../constants/channelCatalog";
import { useAuthContext } from "../context/AuthContext";
import { useCallContext } from "../context/CallContext";
import { useTypes } from "../hooks/useTypes";
import { useMe } from "../hooks/useMe";
import { useUsers } from "../hooks/useUsers";
import { useCompanies } from "../hooks/useCompanies";
import { useAgents } from "../hooks/useAgents";
import {
  importGupshupTemplates,
  getChannelTemplates,
  getChannelEmailDomainStatus,
  sendChannelEmailDomainTest,
  getMetaPages,
  getMetaOauthUrl,
  simulateTelephonyIncoming,
  simulateTelephonyEnd,
  createApifacilInstance,
  getApifacilInstanceQrCode,
  getApifacilInstanceStatus,
  getApifacilInstanceConfig,
  updateApifacilInstanceConfig,
  getChannelWebsiteConfig,
  updateChannelWebsiteConfig,
  regenerateChannelWebsiteTempUrl,
  suggestChannelWebsiteTheme,
  getChannelWebsiteDomainStatus,
  setupChannelWebsiteDomain,
  listChannelWebsiteAudit,
} from "../api/client";

/** Base pública da API (webhooks externos: Gupshup, API Fácil, etc.), mesmo em dev. */
const PUBLIC_API_BASE = "https://secure.xpro.me";

// Helpers
const asBool = (v) => v === 1 || v === true || v === "1";
const asNumOrNull = (v) =>
  v === "" || v === null || v === undefined ? null : Number(v);
const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;
const isEmptyValue = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && v.trim() === "");

// Títulos amigáveis para bases conhecidas; fallback em Title Case
const BASE_TITLES = {
  ecom_cart: "Carrinho pendente",
  ecom_checkout: "Checkout",
  ecom_order: "Pedido feito",
};
const toTitle = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

// *** MOSTRAR APENAS EM ECOMMERCE ***
const ECOM_BASES = ["ecom_cart", "ecom_checkout", "ecom_order"];

/** Campos por tipo (legados preservados) */
const FIELDS_BY_TYPE = {
  // --- DEFAULT / Genérico ---
  [CHANNEL_TYPES.DEFAULT]: [],

  [CHANNEL_TYPES.WHATSAPP_CLOUD]: [
    { name: "id_pagina_facebook", label: "ID WhatsApp (Business ID)", required: true },
    { name: "tokenwhatsapp", label: "Token WhatsApp", required: true },
    { name: "reabrir_contatofuturo", label: "Reabrir Contato Futuro (novas msgs)", type: "boolean" },
    { name: "id_projeto", label: "Projeto (ID)", type: "number" },
    { name: "cadastro_manual", label: "Permitir cadastro manual de leads", type: "boolean", defaultValue: 1 },
    { name: "default", label: "Origem Default", type: "boolean" },
    { name: "envia_primeira_mensagem", label: "Enviar 1ª mensagem ao cadastrar", type: "boolean" },
  ],
  [CHANNEL_TYPES.WHATSAPP_GUPSHUP]: [
    { name: "id_projeto", label: "Projeto (ID)", type: "number" },
    { name: "appidgupshup", label: "App ID Gupshup", required: true },
    { name: "apikeygupshup", label: "API Key Gupshup", required: true },
    { name: "sourcegupshup", label: "Source Gupshup", required: true },
    { name: "sourcenamegupshup", label: "Source Name Gupshup", required: true },
    { name: "callback_url_gupshup", label: "Callback URL Gupshup", type: "readonly" },
    { name: "reabrir_contatofuturo", label: "Reabrir Contato Futuro (novas msgs)", type: "boolean" },
    { name: "cadastro_manual", label: "Permitir cadastro manual de leads", type: "boolean", defaultValue: 1 },
    { name: "default", label: "Origem Default", type: "boolean" },
    { name: "envia_primeira_mensagem", label: "Enviar 1ª mensagem ao cadastrar", type: "boolean" },
  ],
  [CHANNEL_TYPES.WHATSAPP_QR]: [
    {
      name: "numerowhatsapp",
      label: "Instância (instancia_id)",
      helperText:
        "instancia_id da sessão WhatsApp. Preenchido automaticamente ao criar a instância.",
      disabled: true,
    },
    { name: "reabrir_contatofuturo", label: "Reabrir Contato Futuro (novas msgs)", type: "boolean" },
    { name: "id_projeto", label: "Projeto (ID)", type: "number" },
    { name: "cadastro_manual", label: "Permitir cadastro manual de leads", type: "boolean", defaultValue: 1 },
    { name: "default", label: "Origem Default", type: "boolean" },
    { name: "envia_primeira_mensagem", label: "Enviar 1ª mensagem ao cadastrar", type: "boolean" },
  ],
  [CHANNEL_TYPES.SMTP]: [
    { name: "imap", label: "URL SMTP", required: true },
    { name: "porta", label: "Porta SMTP", required: true },
    { name: "ssl", label: "SSL SMTP (0/1)", required: true },
    { name: "imapemail", label: "Login SMTP", required: true },
    { name: "senha", label: "Senha SMTP", required: true },
    { name: "email", label: "Email Remetente", required: true },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.MAILGUN]: [
    { name: "imap", label: "Domínio/Endpoint Mailgun", required: true },
    { name: "imapemail", label: "Login Mailgun", required: true },
    { name: "senha", label: "API Key Mailgun", required: true },
    { name: "email", label: "Email Remetente", required: true },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.TWILIO_PHONE]: [
    {
      name: "telefone",
      label: "Número de Origem",
      required: true,
      helperText:
        "Credenciais e integração da operadora são gerenciadas internamente pela Xpro.",
    },
    { name: "telefonedestino", label: "Número de Destino", required: true },
    { name: "campanha", label: "Campanha" },
    { name: "produto", label: "Produto" },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  // Para Telefonia (PABX), por enquanto usamos apenas os campos comuns
  // (Nome, Empresa, Roteamento, Token gerado automaticamente) e o bloco
  // de instruções de integração. Campos específicos ficam ocultos.
  [CHANNEL_TYPES.PABX]: [],
  [CHANNEL_TYPES.SMS]: [
    {
      name: "telefone",
      label: "Número do canal (To)",
      required: true,
      helperText:
        "Número que receberá SMS inbound (ex.: +5511999999999). As credenciais do provedor são gerenciadas internamente.",
    },
  ],
  [CHANNEL_TYPES.FB_MESSENGER]: [
    { name: "id_pagina_facebook", label: "ID da Página Facebook", required: true },
    {
      name: "token_messenger",
      label: "Token da Página (Page Access Token)",
      required: true,
      multiline: true,
      helperText:
        "Token de acesso gerado no painel da Meta para a página conectada. Mantenha este valor em sigilo.",
    },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.IG_MESSENGER]: [
    { name: "id_pagina_facebook", label: "ID do Instagram Business (ig-user-id)", required: true },
    {
      name: "token_instagram",
      label: "Token do Instagram (Access Token)",
      required: true,
      multiline: true,
      helperText:
        "Token de acesso gerado no painel da Meta para a conta do Instagram conectada. Mantenha este valor em sigilo.",
    },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.APP_IOS]: [
    { name: "permitir_login_app", label: "Permitir Login pelo App", type: "boolean", defaultValue: 1 },
    { name: "idaplicativo", label: "Aplicativo (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.APP_ANDROID]: [
    { name: "permitir_login_app", label: "Permitir Login pelo App", type: "boolean", defaultValue: 1 },
    { name: "idaplicativo", label: "Aplicativo (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.WORDPRESS]: [
    { name: "token", label: "Token/Webhook Secret", required: true },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.GOOGLE_ADS]: [
    { name: "token", label: "Token/Webhook Secret", required: true },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],
  [CHANNEL_TYPES.META_ADS]: [
    { name: "token", label: "Token/Webhook Secret", required: true },
    { name: "id_projeto", label: "Projeto (ID)", type: "number", required: true },
  ],

  // === E-mail via SES (AWS) ===
  [CHANNEL_TYPES.EMAIL_AWS_SES]: [
    {
      name: "email",
      label: "Endereço de e-mail",
      required: true,
      helperText:
        "Usaremos este endereço como remetente padrão deste canal. Você pode usar o domínio padrão da Xpro (workspace.xpro.me) ou, após configurar um domínio próprio, usar esse domínio aqui.",
    },
    {
      name: "email_domain",
      label: "Domínio próprio (ex: suaempresa.com)",
      required: false,
      helperText:
        "Opcional. Se deixar em branco, usaremos um domínio gerenciado pela Xpro (xpro.me / workspace.xpro.me). Ao informar um domínio próprio, o endereço de e-mail acima deve usar exatamente esse domínio.",
    },
    { name: "id_projeto", label: "Projeto (ID)", type: "number" },
  ],

  // === Ecommerce: campos de mapeamento aparecem como cascatas dinâmicas
  [CHANNEL_TYPES.ECOMMERCE]: [],
};

// Campos comuns
const COMMON_FIELDS = [
  { name: "nome", label: "Nome do Canal", required: true },
  { name: "status", label: "Ativo", type: "boolean", defaultValue: 1 },
];

function FieldControl({ field, value, onChange }) {
  const { name, label, type, required, disabled, multiline, helperText } = field;

  // Hook SEMPRE no topo (evita chamada condicional de hook)
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (type === "readonly") {
    const isGupshupCallback = name === "callback_url_gupshup";
    const hasUrl = Boolean(value);

    const helperText = isGupshupCallback
      ? hasUrl
        ? "Copie e cole esta URL no painel da Gupshup (Callback URL)."
        : "A URL ficará disponível aqui após salvar o canal."
      : "";

    return (
      <Box>
        <TextField
          label={label}
          value={value ?? ""}
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment:
              hasUrl && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopy}
                    edge="end"
                    aria-label="Copiar valor"
                    color={copied ? "success" : "primary"}
                    size="small"
                  >
                    {copied ? <CheckIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </InputAdornment>
              ),
          }}
          helperText={helperText}
        />
      </Box>
    );
  }

  if (type === "boolean") {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={asBool(value)}
            onChange={(e) => onChange(name, e.target.checked ? 1 : 0)}
          />
        }
        label={label}
      />
    );
  }

  const isSecretField =
    typeof name === "string" &&
    (/token|secret|senha|api[-_ ]?key/i.test(name) ||
      (typeof label === "string" && /token|secret|senha|api[-_ ]?key/i.test(label)));

  const readOnly = field.readOnly;
  const onClick = field.onClick;
  const onFocus = field.onFocus;

  return (
    <TextField
      label={label}
      name={name}
      required={!!required}
      disabled={!!disabled}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
      fullWidth
      type={field.type === "number" ? "number" : isSecretField ? "password" : "text"}
      autoComplete={isSecretField ? "new-password" : undefined}
      multiline={!!multiline}
      rows={multiline ? 4 : undefined}
      helperText={helperText}
      InputProps={readOnly ? { readOnly: true } : undefined}
      onClick={onClick}
      onFocus={onFocus}
    />
  );
}

function TypeCard({ card, selected, onClick }) {
  const Icon = card.icon;
  return (
    <Card
      onClick={onClick}
      role="button"
      aria-pressed={selected}
      sx={(theme) => ({
        position: "relative",
        p: 2.75,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: "pointer",
        transition: "box-shadow .2s ease, transform .05s ease",
        "&:hover": { boxShadow: 4 },
        boxShadow: selected ? `inset 0 0 0 2px ${theme.palette.primary.main}` : "none",
        zIndex: selected ? 2 : 1,
        minHeight: 100,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      })}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Icon sx={{ color: card.color ?? "inherit" }} />
        <Typography variant="subtitle1" fontWeight={700}>
          {card.label}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {card.description}
      </Typography>
      {selected && (
        <Box mt="auto">
          <Chip size="small" label="Selecionado" color="primary" />
        </Box>
      )}
    </Card>
  );
}

// Normaliza id_agente do canal (API pode retornar id_agente ou agentId)
const channelIdAgente = (ch) => {
  const v = ch?.id_agente ?? ch?.agentId;
  return v != null && v !== "" ? String(v) : null;
};

export default function AddChannelForm({ channel, onClose, onSubmit, isLoading }) {
  const { userData } = useAuthContext();
  const { clearIncomingCall } = useCallContext();
  const { me } = useMe();
  const meData = me?.data ?? null;

  const getWorkspaceDomain = () => {
    const slug =
      (typeof window !== "undefined"
        ? window.localStorage.getItem("company")
        : "") || "";
    if (!slug) return "xpro.me";
    return `${slug}.xpro.me`;
  };

  // aceita 0 (DEFAULT)
  const initialTipo = useMemo(
    () => (channel?.tipo != null ? Number(channel.tipo) : null),
    [channel]
  );
  const isEditing = Boolean(channel?.id);

  const [step, setStep] = useState(isEditing ? 2 : 1);
  const [tipo, setTipo] = useState(initialTipo);
  /** Ref para garantir que o id_agente selecionado chegue ao payload mesmo se formData for sobrescrito por efeitos */
  const idAgenteRef = useRef(channelIdAgente(channel));
  /** Ref para garantir que o id_chatbot selecionado chegue ao payload mesmo se formData for sobrescrito por efeitos */
  const idChatbotRef = useRef(null);
  /** Já aplicamos a normalização legado→id_chatbot para este `channel.id` (evita reexecutar a cada refetch de usuários). */
  const chatbotRoutingSyncedChannelIdRef = useRef(null);
  /** Evita remesclar o mesmo canal quando `channel` só muda de referência (refetch) — isso sobrescrevia idusuario/id_chatbot */
  const lastMergedChannelIdRef = useRef(null);

  const [formData, setFormData] = useState(() => ({
    ...channel,
    // comuns
    id_usuario: channel?.id_usuario || userData?.id || null,
    id_usuario_ultima_alteracao: userData?.id,
    idusuario: channel?.idusuario ?? null,
    // Chatbot no roteamento (internamente persistimos no legado via `idusuario` quando selecionado)
    id_chatbot: null,
    idusuarios:
      Array.isArray(channel?.id_usuarios_padrao)
        ? channel.id_usuarios_padrao.map(Number)
        : Array.isArray(channel?.idusuarios)
          ? channel.idusuarios.map(Number)
          : channel?.idusuario
            ? [Number(channel.idusuario)]
            : [],
    id_agente: channelIdAgente(channel),
    status: channel?.status ?? 1,
    id_tipo: channel?.id_tipo ?? null,
    id_tipo1: channel?.id_tipo1 ?? null,
    id_tipo2: channel?.id_tipo2 ?? null,
    id_tipo3: channel?.id_tipo3 ?? null,
    // mapeamentos (qualquer base *_type_id é suportada de forma dinâmica)
    ecom_cart_type_id: channel?.ecom_cart_type_id ?? null,
    ecom_cart_type1_id: channel?.ecom_cart_type1_id ?? null,
    ecom_cart_type2_id: channel?.ecom_cart_type2_id ?? null,
    ecom_cart_type3_id: channel?.ecom_cart_type3_id ?? null,

    ecom_checkout_type_id: channel?.ecom_checkout_type_id ?? null,
    ecom_checkout_type1_id: channel?.ecom_checkout_type1_id ?? null,
    ecom_checkout_type2_id: channel?.ecom_checkout_type2_id ?? null,
    ecom_checkout_type3_id: channel?.ecom_checkout_type3_id ?? null,

    ecom_order_type_id: channel?.ecom_order_type_id ?? null,
    ecom_order_type1_id: channel?.ecom_order_type1_id ?? null,
    ecom_order_type2_id: channel?.ecom_order_type2_id ?? null,
    ecom_order_type3_id: channel?.ecom_order_type3_id ?? null,
  }));
  const [errorMessage, setErrorMessage] = useState(null);
  const [isImportingTemplates, setIsImportingTemplates] = useState(false);
  const [importTemplatesResult, setImportTemplatesResult] = useState(null);
  const [importTemplatesError, setImportTemplatesError] = useState(null);
  const [channelTemplates, setChannelTemplates] = useState([]);
  const [channelTemplatesLoading, setChannelTemplatesLoading] = useState(false);
  const [emailDomainStatus, setEmailDomainStatus] = useState(null);
  const [emailDomainLoading, setEmailDomainLoading] = useState(false);
  const [emailDomainError, setEmailDomainError] = useState(null);
  const [websiteConfig, setWebsiteConfig] = useState(null);
  const [websiteConfigLoading, setWebsiteConfigLoading] = useState(false);
  const [websiteConfigMessage, setWebsiteConfigMessage] = useState(null);
  const [websiteThemePrompt, setWebsiteThemePrompt] = useState("");
  const [websiteDomainInfo, setWebsiteDomainInfo] = useState(null);
  const [websiteAudit, setWebsiteAudit] = useState([]);
  const [websiteAuditActionFilter, setWebsiteAuditActionFilter] = useState("all");
  const [websiteAuditPeriodFilter, setWebsiteAuditPeriodFilter] = useState("7d");
  const [expandedWebsiteAuditIds, setExpandedWebsiteAuditIds] = useState([]);
  // Helper para tratar domínios "compartilhados" da Xpro (xpro.me / vazio) como não personalizados
  const isSharedXproDomain = (domain) => {
    const d = String(domain || "").trim().toLowerCase();
    const sharedRoot = "xpro.me";
    return !d || d === sharedRoot;
  };
  const [showCustomEmailDomain, setShowCustomEmailDomain] = useState(
    Boolean(channel?.email_domain && !isSharedXproDomain(channel.email_domain))
  );
  const [showDnsDetails, setShowDnsDetails] = useState(false);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [testEmailMessage, setTestEmailMessage] = useState(null);
  const [clientSideError, setClientSideError] = useState(null);
  const [copiedIncomingUrl, setCopiedIncomingUrl] = useState(false);
  const [copiedIncomingBody, setCopiedIncomingBody] = useState(false);
  const [copiedEndUrl, setCopiedEndUrl] = useState(false);
  const [copiedEndBody, setCopiedEndBody] = useState(false);
  const [apifacilQrData, setApifacilQrData] = useState(null);
  const [apifacilLoading, setApifacilLoading] = useState(false);
  const [apifacilStatus, setApifacilStatus] = useState(null);
  const [apifacilQrExpiresAt, setApifacilQrExpiresAt] = useState(null);
  const [apifacilStatusHint, setApifacilStatusHint] = useState(null);
  const [apifacilConfigLoading, setApifacilConfigLoading] = useState(false);
  const [apifacilConfigMessage, setApifacilConfigMessage] = useState(null);
  const [apifacilConfigSeverity, setApifacilConfigSeverity] = useState("info");
  const [apifacilConfig, setApifacilConfig] = useState(() => ({
    rejeitar_chamadas: false,
  }));

  // Padrões da aplicação (mantidos ocultos da UI)
  const APIFACIL_DEFAULTS = {
    webhook_ativo: true,
    ativar_notificacao_instancia: true,
    ativar_atualizacoes_interacao: true,
    ativar_delay_envio_resposta: false,
    // tipos_envio é uma "lista de bloqueio" na doc; mantemos vazio para não bloquear nada
    tipos_envio: [],
  };

  // Telefonia: simulação de chamada (teste)
  const [telephonySimulateIncomingLoading, setTelephonySimulateIncomingLoading] = useState(false);
  const [telephonySimulateEndLoading, setTelephonySimulateEndLoading] = useState(false);
  const [simulatedCallSid, setSimulatedCallSid] = useState(null);
  const [telephonySimulateMessage, setTelephonySimulateMessage] = useState(null);
  const [telephonySimulateSeverity, setTelephonySimulateSeverity] = useState("info");
  // Parâmetros personalizáveis da simulação (defaults pré-preenchidos)
  const [simIdChamada, setSimIdChamada] = useState(() => `test-${Date.now()}`);
  const [simTelefone, setSimTelefone] = useState("5511999998888");
  const [simAtendenteValue, setSimAtendenteValue] = useState("");
  const simAtendenteInitialized = useRef(false);
  const [simTempoEspera, setSimTempoEspera] = useState("5");
  const [simDuracao, setSimDuracao] = useState("30");
  const [simAtendida, setSimAtendida] = useState("1");

  // Pré-preenche "Quem recebe a notificação" com id_interno do usuário logado (fonte: userData do login ou /users/me)
  useEffect(() => {
    if (simAtendenteInitialized.current) return;
    const source = userData || meData;
    if (!source) return;
    const v = String(
      source.id_interno ?? source.codigo_agente ?? source.email ?? ""
    ).trim();
    if (v) {
      setSimAtendenteValue(v);
      simAtendenteInitialized.current = true;
    }
  }, [userData, meData]);

  // Meta (Facebook / Instagram) helper state
  const [metaPages, setMetaPages] = useState([]);
  const [loadingMetaPages, setLoadingMetaPages] = useState(false);
  const [metaPagesError, setMetaPagesError] = useState(null);
  const [metaAccessToken, setMetaAccessToken] = useState(null);
  const [metaOauthError, setMetaOauthError] = useState(null);
  const [metaConnecting, setMetaConnecting] = useState(false);

  const topRef = useRef(null);

  // === Tipos (árvore)
  const { data: typesResp, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const rootTypes = useMemo(() => typesResp?.types || [], [typesResp?.types]);

  // === Usuários: humanos e usuários tipo chatbot para roteamento; hook padrão filtra só "humanos"
  const { data: usersResp, isLoading: loadingUsers } = useUsers(1, 1000, "", "todos");
  const allUsers = useMemo(() => usersResp?.users || [], [usersResp?.users]);
  const activeUsers = useMemo(
    () => allUsers.filter((u) => u.status === 1 || u.ativo === 1 || u.active === true),
    [allUsers]
  );
  /** ura=1 no banco; API pode serializar como número, string, boolean ou omitir em caches antigos */
  const isChatBotUser = (u) => {
    if (!u || typeof u !== "object") return false;
    const r = u.ura;
    if (r === true || r === 1) return true;
    if (typeof r === "number" && Number.isFinite(r) && r === 1) return true;
    if (typeof r === "string" && String(r).trim() === "1") return true;
    const n = Number(r);
    return Number.isFinite(n) && n === 1;
  };
  const activeHumanUsers = useMemo(
    () => activeUsers.filter((u) => !isChatBotUser(u)),
    [activeUsers]
  );
  const allHumanUsers = useMemo(
    () => allUsers.filter((u) => !isChatBotUser(u)),
    [allUsers]
  );
  const allChatbotUsers = useMemo(
    () => allUsers.filter((u) => isChatBotUser(u)),
    [allUsers]
  );
  /** MUI Select exige o mesmo tipo que MenuItem.value; API pode trazer id numérico — normaliza para string. */
  const chatbotSelectValue = useMemo(() => {
    const v = formData.id_chatbot;
    if (v === undefined || v === null || v === "") return "";
    return String(v);
  }, [formData.id_chatbot]);

  /**
   * Opções do select: sempre incluir o id já escolhido no canal (ex.: inativo ou fora da lista "ativa").
   * Antes usávamos só activeChatbotUsers quando havia algum ativo — aí o id 74 sumia das opções → MUI out-of-range.
   */
  const chatbotMenuOptions = useMemo(() => {
    const base = allChatbotUsers;
    const v = chatbotSelectValue;
    let list;
    if (!v) list = base;
    else if (base.some((u) => String(u.id) === v)) list = base;
    else {
      const fromAll = allUsers.find((u) => String(u.id) === v && isChatBotUser(u));
      if (fromAll) list = [fromAll, ...base];
      else {
        const any = allUsers.find((u) => String(u.id) === v);
        if (any)
          list = [{ ...any, nome: any.nome ?? any.name ?? `Usuário #${v}` }, ...base];
        else {
          const n = Number(v);
          list = [
            {
              id: Number.isFinite(n) ? n : v,
              nome: `ID ${v} (não listado)`,
              ura: 1,
            },
            ...base,
          ];
        }
      }
    }
    // Dois MenuItems com o mesmo value quebram o Select do MUI (clique não altera o valor).
    const seen = new Set();
    return list.filter((u) => {
      const id = String(u?.id);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [allChatbotUsers, allUsers, chatbotSelectValue]);

  const selectedChatbotOption = useMemo(() => {
    if (!chatbotSelectValue) return null;
    const found = chatbotMenuOptions.find((u) => String(u.id) === chatbotSelectValue);
    if (found) return found;
    const n = Number(chatbotSelectValue);
    return {
      id: Number.isFinite(n) ? n : chatbotSelectValue,
      nome: `Chatbot #${chatbotSelectValue}`,
      ura: 1,
    };
  }, [chatbotMenuOptions, chatbotSelectValue]);

  const { data: agentsData } = useAgents(1, 200, "");
  const agentsList = agentsData?.agents ?? [];

  /** Apenas um destinos de roteamento ativo: humanos, chatbot (usuário URA) ou agente de IA */
  const hasHumanRouting = useMemo(
    () => Array.isArray(formData.idusuarios) && formData.idusuarios.length > 0,
    [formData.idusuarios]
  );
  const hasChatbotRouting = useMemo(
    () => formData.id_chatbot != null && formData.id_chatbot !== "",
    [formData.id_chatbot]
  );
  const hasAgentRouting = useMemo(
    () => formData.id_agente != null && formData.id_agente !== "",
    [formData.id_agente]
  );

  // Normaliza roteamento legado (idusuario) em id_chatbot + idusuarios(humanos).
  // useEffect (não useLayoutEffect): evita competir com listbox/popover do campo de chatbot no mesmo ciclo de commit.
  useEffect(() => {
    if (!Array.isArray(allUsers) || !allUsers.length) return;

    const chId =
      channel?.id != null && channel?.id !== "" ? String(channel.id) : "__new__";

    if (chatbotRoutingSyncedChannelIdRef.current === chId) {
      return;
    }

    const chatbotIdSet = new Set(
      allChatbotUsers.map((u) => Number(u.id)).filter((n) => !Number.isNaN(n))
    );

    setFormData((prev) => {
      const prevSelectedIds = Array.isArray(prev.idusuarios)
        ? prev.idusuarios.map(Number)
        : [];

      const candidateFromIdusuario =
        prev?.idusuario != null && prev?.idusuario !== ""
          ? Number(prev.idusuario)
          : null;

      let candidateChatbotId = null;
      if (candidateFromIdusuario != null && chatbotIdSet.has(candidateFromIdusuario)) {
        candidateChatbotId = candidateFromIdusuario;
      } else {
        const found = prevSelectedIds.find((id) => chatbotIdSet.has(Number(id)));
        if (found != null) candidateChatbotId = Number(found);
      }

      const nextIdChatbot = candidateChatbotId != null ? String(candidateChatbotId) : null;
      const nextIdusuariosHumans = prevSelectedIds.filter((id) => !chatbotIdSet.has(Number(id)));

      idChatbotRef.current = nextIdChatbot;

      return {
        ...prev,
        id_chatbot: nextIdChatbot,
        idusuarios: nextIdusuariosHumans,
        // Mantém idusuario legado sincronizado para compatibilidade com rotas legadas
        idusuario:
          nextIdChatbot != null
            ? Number(nextIdChatbot)
            : nextIdusuariosHumans.length
              ? nextIdusuariosHumans[0]
              : null,
      };
    });

    chatbotRoutingSyncedChannelIdRef.current = chId;
  }, [allUsers, allChatbotUsers, channel?.id]);

  // === Projetos (empresas) para o select de id_projeto
  const { data: companiesResp, isLoading: loadingCompanies } = useCompanies(1, 1000, "");
  const companyList = companiesResp?.companies || [];

  // Listener global para receber o accessToken da janela de OAuth da Meta
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.provider !== "meta" || !data.accessToken) return;

      const token = data.accessToken;
      setMetaAccessToken(token);
      setMetaOauthError(null);

      setFormData((prev) => {
        const next = { ...prev };
        if (tipo === CHANNEL_TYPES.FB_MESSENGER) {
          next.token_messenger = token;
        } else if (tipo === CHANNEL_TYPES.IG_MESSENGER) {
          next.token_instagram = token;
        } else if (tipo === CHANNEL_TYPES.WHATSAPP_CLOUD) {
          next.tokenwhatsapp = token;
        }
        return next;
      });
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [tipo, setFormData]);

  // Busca automática das páginas da Meta assim que houver um token válido
  useEffect(() => {
    const isMetaChannel =
      tipo === CHANNEL_TYPES.FB_MESSENGER || tipo === CHANNEL_TYPES.IG_MESSENGER;

    const tokenValue =
      metaAccessToken ||
      (tipo === CHANNEL_TYPES.FB_MESSENGER
        ? formData.token_messenger
        : tipo === CHANNEL_TYPES.IG_MESSENGER
          ? formData.token_instagram
          : null);

    if (!isMetaChannel || !tokenValue) {
      setMetaPages([]);
      setMetaPagesError(null);
      setLoadingMetaPages(false);
      return;
    }

    let cancelled = false;

    const loadPages = async () => {
      setLoadingMetaPages(true);
      setMetaPagesError(null);
      try {
        const res = await getMetaPages({
          accessToken: tokenValue,
          includeInstagram: true,
        });
        if (cancelled) return;

        const pages = Array.isArray(res?.pages) ? res.pages : [];
        setMetaPages(pages);

        if (!pages.length) {
          const isInstagramChannel = tipo === CHANNEL_TYPES.IG_MESSENGER;
          setMetaPagesError(
            isInstagramChannel
              ? "Conseguimos conectar à sua conta Meta, mas não encontramos nenhuma Página ou conta de Instagram elegível para este canal."
              : "Conseguimos conectar à sua conta Meta, mas não encontramos nenhuma Página elegível para este canal."
          );
        } else if (formData.id_pagina_facebook) {
          const exists = pages.some((p) => {
            const idForChannel =
              tipo === CHANNEL_TYPES.IG_MESSENGER
                ? p.instagramId
                : p.pageId;
            return (
              idForChannel &&
              String(idForChannel) === String(formData.id_pagina_facebook)
            );
          });
          // Se o usuário acabou de reconectar a Meta (metaAccessToken definido) e a página
          // anteriormente salva não estiver mais disponível para esse token, limpamos o campo.
          // Em modo de edição com token já persistido, preservamos o valor salvo para exibição.
          if (!exists && metaAccessToken) {
            setFormData((prev) => ({
              ...prev,
              id_pagina_facebook: "",
            }));
          }
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("Erro ao buscar páginas da Meta:", err);
        const msg =
          err?.message ||
          err?.response?.data?.message ||
          "Não foi possível listar as páginas da sua conta Meta. " +
            "Feche esta tela, clique novamente em \"Conectar com Meta\" e, na etapa de permissões, " +
            "confira se a opção de acesso às páginas está marcada antes de concluir.";
        setMetaPages([]);
        setMetaPagesError(msg);
      } finally {
        if (!cancelled) {
          setLoadingMetaPages(false);
        }
      }
    };

    loadPages();

    return () => {
      cancelled = true;
    };
  }, [tipo, metaAccessToken, formData.token_messenger, formData.token_instagram, formData.id_pagina_facebook]);

  // Ao carregar páginas com token da página (pageAccessToken), sincronizar token do canal
  // com a página selecionada (envio de mensagens exige Page token, não User token).
  useEffect(() => {
    if (
      (tipo !== CHANNEL_TYPES.FB_MESSENGER && tipo !== CHANNEL_TYPES.IG_MESSENGER) ||
      !formData.id_pagina_facebook ||
      !metaPages.length
    )
      return;
    const page = metaPages.find((p) => {
      const id = tipo === CHANNEL_TYPES.IG_MESSENGER ? p.instagramId : p.pageId;
      return id && String(id) === String(formData.id_pagina_facebook);
    });
    if (!page?.pageAccessToken) return;
    setFormData((prev) => {
      const tokenField =
        tipo === CHANNEL_TYPES.IG_MESSENGER ? "token_instagram" : "token_messenger";
      if (prev[tokenField] === page.pageAccessToken) return prev;
      return { ...prev, [tokenField]: page.pageAccessToken };
    });
  }, [tipo, formData.id_pagina_facebook, metaPages]);

  // Aplica defaults ao escolher tipo (inclui 0)
  useEffect(() => {
    if (tipo == null) return;
    const fields = FIELDS_BY_TYPE[tipo] || [];
    setFormData((prev) => {
      const next = { ...prev, tipo };
      fields.forEach((f) => {
        if (f.defaultValue != null && prev[f.name] == null) next[f.name] = f.defaultValue;
      });
      COMMON_FIELDS.forEach((f) => {
        if (f.defaultValue != null && prev[f.name] == null) next[f.name] = f.defaultValue;
      });
      if (tipo === CHANNEL_TYPES.WHATSAPP_GUPSHUP) {
        const workspaceSlug =
          (typeof window !== "undefined"
            ? window.localStorage.getItem("company")
            : "") || "";
        const idOrigem = prev.id ?? channel?.id ?? null;
        if (idOrigem && workspaceSlug) {
          const qs = new URLSearchParams({
            company: workspaceSlug,
            originId: String(idOrigem),
          });
          next.callback_url_gupshup = `${PUBLIC_API_BASE}/webhooks/whatsapp/gupshup?${qs.toString()}`;
        } else {
          next.callback_url_gupshup = "";
        }
      }
      // Para Telefonia (PABX), gera token apenas em canal NOVO (sem id). Em edição, preserva o token existente.
      const isNewPabxChannel = !(next.id ?? channel?.id);
      if (tipo === CHANNEL_TYPES.PABX && !next.token && isNewPabxChannel) {
        const rand =
          (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
        next.token = `tel_${rand}`;
      }
      return next;
    });

    // *** se sair de Ecommerce, zera todos os campos ecom_* ***
    if (tipo !== CHANNEL_TYPES.ECOMMERCE) {
      setFormData((prev) => {
        const next = { ...prev };
        ECOM_BASES.forEach((base) => {
          next[`${base}_type_id`] = null;
          next[`${base}_type1_id`] = null;
          next[`${base}_type2_id`] = null;
          next[`${base}_type3_id`] = null;
        });
        return next;
      });
    }
  }, [tipo, channel?.id]);

  // edição: seta tipo mesmo quando é 0
  useEffect(() => {
    if (isEditing && initialTipo != null && tipo == null) setTipo(initialTipo);
  }, [isEditing, initialTipo, tipo]);

  const hasSecret = (fieldName) => {
    const flagName = `has_${fieldName}`;
    // prioridade: estado local (permite "destravar" o campo) e, como fallback, o valor vindo do canal
    if (Object.prototype.hasOwnProperty.call(formData, flagName)) {
      return Boolean(formData[flagName]);
    }
    if (!channel) return false;
    return Boolean(channel[flagName]);
  };

  const clearSecretField = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: "",
      [`has_${fieldName}`]: false,
    }));
  };

  // Scroll suave ao entrar no passo 2
  useEffect(() => {
    if (step === 2 && topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  useEffect(() => {
    if (!isEditing || !channel) return;
    const cid =
      channel.id != null && channel.id !== "" ? String(channel.id) : null;
    if (!cid) return;
    if (lastMergedChannelIdRef.current === cid) return;
    lastMergedChannelIdRef.current = cid;

    const idAgenteFromChannel = channelIdAgente(channel);
    setFormData((prev) => {
      // id_chatbot + idusuario de roteamento: mesma ideia do id_agente — o merge com `channel` pode
      // rodar depois do efeito que normaliza legado→id_chatbot e apagaria a seleção na edição.
      const routingChatbot =
        idChatbotRef.current ??
        (prev?.id_chatbot != null && prev?.id_chatbot !== "" ? String(prev.id_chatbot) : null);

      const merged = { ...prev, ...channel };
      // id_agente: ref é a fonte de verdade (seleção do usuário); não sobrescrever com null vindo de channel
      const kept =
        idAgenteRef.current ??
        idAgenteFromChannel ??
        (prev?.id_agente != null && prev?.id_agente !== "" ? prev.id_agente : null);
      merged.id_agente = kept != null && kept !== "" ? String(kept) : null;
      if (merged.id_agente != null) idAgenteRef.current = merged.id_agente;

      if (routingChatbot != null && routingChatbot !== "") {
        merged.id_chatbot = routingChatbot;
        const n = Number(routingChatbot);
        if (!Number.isNaN(n)) {
          merged.idusuario = n;
          merged.idusuarios = [];
        }
      }

      return merged;
    });
    if (channel?.tipo != null) setTipo(Number(channel.tipo));
  }, [isEditing, channel]);

  const onChange = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  // Gera um novo token de telefonia (invalida o anterior; usar quando o usuário quiser rotacionar o token)
  const generateNewTelephonyToken = () => {
    const rand =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    onChange("token", `tel_${rand}`);
  };

  const handleSimulateIncomingCall = async () => {
    setTelephonySimulateMessage(null);
    const token = formData.token?.trim();
    const channelId = formData.id || channel?.id;
    const projectId = formData.id_projeto;
    if (!token) {
      setTelephonySimulateSeverity("warning");
      setTelephonySimulateMessage("Preencha o token e salve o canal para testar.");
      return;
    }
    if (!channelId) {
      setTelephonySimulateSeverity("warning");
      setTelephonySimulateMessage("Salve o canal primeiro para simular uma chamada.");
      return;
    }
    if (!projectId) {
      setTelephonySimulateSeverity("warning");
      setTelephonySimulateMessage("Vincule o canal a um projeto para testar a integração.");
      return;
    }
    setTelephonySimulateIncomingLoading(true);
    try {
      const sid = (simIdChamada && simIdChamada.trim()) || `test-${Date.now()}`;
      const telefone = (simTelefone && simTelefone.trim()) || "5511999998888";
      const sourceUser = userData || meData;
      const defaultAtendente = String(
        sourceUser?.id_interno ??
          sourceUser?.codigo_agente ??
          sourceUser?.email ??
          ""
      ).trim();
      const atendenteRaw =
        (simAtendenteValue && simAtendenteValue.trim()) || defaultAtendente;
      const payloadIncoming = {
        id_chamada: sid,
        telefone,
        data: new Date().toISOString().slice(0, 19).replace("T", " "),
        status: "ringing",
        token,
      };
      if (atendenteRaw.includes("@")) {
        payloadIncoming.emailusuario = atendenteRaw;
      } else if (atendenteRaw) {
        payloadIncoming.codigo_agente = atendenteRaw;
      }
      const res = await simulateTelephonyIncoming(payloadIncoming);
      setSimulatedCallSid(sid);
      setTelephonySimulateSeverity("success");
      setTelephonySimulateMessage(
        "Chamada simulada! Com a tela de Chat aberta, a notificação de chamada deve aparecer. Caso contrário, confira a lista de solicitações."
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Erro ao simular chamada.";
      setTelephonySimulateSeverity("error");
      setTelephonySimulateMessage(msg);
      setSimulatedCallSid(null);
    } finally {
      setTelephonySimulateIncomingLoading(false);
    }
  };

  const handleSimulateEndCall = async () => {
    setTelephonySimulateMessage(null);
    const token = formData.token?.trim();
    if (!token) {
      setTelephonySimulateSeverity("warning");
      setTelephonySimulateMessage("Token do canal é obrigatório.");
      return;
    }
    const endSid = (simIdChamada && simIdChamada.trim()) || "";
    if (!endSid) {
      setTelephonySimulateSeverity("info");
      setTelephonySimulateMessage("Informe o ID da chamada acima (mesmo usado em \"Chamada recebida\").");
      return;
    }
    setTelephonySimulateEndLoading(true);
    try {
      const tempoEspera = parseInt(simTempoEspera, 10);
      const duracao = parseInt(simDuracao, 10);
      const atendida = parseInt(simAtendida, 10);
      await simulateTelephonyEnd({
        id_chamada: endSid,
        token,
        tempo_espera: Number.isNaN(tempoEspera) ? 5 : tempoEspera,
        duracao: Number.isNaN(duracao) ? 30 : duracao,
        atendida: Number.isNaN(atendida) ? 1 : atendida,
        status: "completed",
      });
      setTelephonySimulateSeverity("success");
      setTelephonySimulateMessage("Chamada encerrada simulada. A notificação de chamada deve ter sido fechada.");
      setSimulatedCallSid(null);
      // Fecha o CallNotification localmente (o socket callEnded pode não chegar dependendo do servidor de sockets)
      if (typeof clearIncomingCall === "function") clearIncomingCall();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Erro ao simular encerramento.";
      setTelephonySimulateSeverity("error");
      setTelephonySimulateMessage(msg);
    } finally {
      setTelephonySimulateEndLoading(false);
    }
  };

  const selectedCard = findCardById(tipo);

  // ======== helpers para árvore de tipos / subtipos (roteamento padrão) ========
  const selectedRoot = useMemo(
    () => rootTypes.find((t) => Number(t.id) === Number(formData.id_tipo)),
    [rootTypes, formData.id_tipo]
  );
  const level1 = useMemo(() => (Array.isArray(selectedRoot?.children) ? selectedRoot.children : []), [selectedRoot]);
  const selectedL1 = useMemo(
    () => level1.find((s) => Number(s.id) === Number(formData.id_tipo1)),
    [level1, formData.id_tipo1]
  );
  const level2 = useMemo(() => (Array.isArray(selectedL1?.children) ? selectedL1.children : []), [selectedL1]);
  const selectedL2 = useMemo(
    () => level2.find((u) => Number(u.id) === Number(formData.id_tipo2)),
    [level2, formData.id_tipo2]
  );
  const level3 = useMemo(() => (Array.isArray(selectedL2?.children) ? selectedL2.children : []), [selectedL2]);

  const handleRootTypeChange = (val) => {
    onChange("id_tipo", asNumOrNull(val));
    onChange("id_tipo1", null);
    onChange("id_tipo2", null);
    onChange("id_tipo3", null);
  };
  const handleLvl1Change = (val) => {
    onChange("id_tipo1", asNumOrNull(val));
    onChange("id_tipo2", null);
    onChange("id_tipo3", null);
  };
  const handleLvl2Change = (val) => {
    onChange("id_tipo2", asNumOrNull(val));
    onChange("id_tipo3", null);
  };
  const handleLvl3Change = (val) => {
    onChange("id_tipo3", asNumOrNull(val));
  };

  // ======== helpers genéricos para cascatas dinâmicas (qualquer base *_type_id) ========
  const getCascadeLevels = (base) => {
    const rootVal = formData[`${base}_type_id`];
    const root = rootTypes.find((t) => Number(t.id) === Number(rootVal));
    const l1 = Array.isArray(root?.children) ? root.children : [];
    const sub1Val = formData[`${base}_type1_id`];
    const n1 = l1.find((s) => Number(s.id) === Number(sub1Val));
    const l2 = Array.isArray(n1?.children) ? n1.children : [];
    const sub2Val = formData[`${base}_type2_id`];
    const n2 = l2.find((u) => Number(u.id) === Number(sub2Val));
    const l3 = Array.isArray(n2?.children) ? n2.children : [];
    return { l1, l2, l3 };
  };

  const handleCascadeChange = (base, level, val) => {
    if (level === 0) {
      onChange(`${base}_type_id`, asNumOrNull(val));
      onChange(`${base}_type1_id`, null);
      onChange(`${base}_type2_id`, null);
      onChange(`${base}_type3_id`, null);
    }
    if (level === 1) {
      onChange(`${base}_type1_id`, asNumOrNull(val));
      onChange(`${base}_type2_id`, null);
      onChange(`${base}_type3_id`, null);
    }
    if (level === 2) {
      onChange(`${base}_type2_id`, asNumOrNull(val));
      onChange(`${base}_type3_id`, null);
    }
    if (level === 3) {
      onChange(`${base}_type3_id`, asNumOrNull(val));
    }
  };

  const loadChannelTemplates = async (originIdToLoad) => {
    if (!originIdToLoad) return;
    setChannelTemplatesLoading(true);
    try {
      const { templates } = await getChannelTemplates(originIdToLoad);
      setChannelTemplates(Array.isArray(templates) ? templates : []);
    } catch (err) {
      setChannelTemplates([]);
    } finally {
      setChannelTemplatesLoading(false);
    }
  };

  useEffect(() => {
    const originId = formData.id || channel?.id;
    if (formData.tipo === CHANNEL_TYPES.WHATSAPP_GUPSHUP && originId) {
      loadChannelTemplates(originId);
    } else {
      setChannelTemplates([]);
    }
  }, [formData.id, formData.tipo, channel?.id]);

  const refreshEmailDomainStatus = () => {
    if (!isEditing || !channel?.id || tipo !== CHANNEL_TYPES.EMAIL_AWS_SES) return;
    setEmailDomainLoading(true);
    setEmailDomainError(null);
    getChannelEmailDomainStatus(channel.id)
      .then((res) => {
        setEmailDomainStatus(res || null);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Erro ao carregar status de domínio de e-mail:", err);
        setEmailDomainError(
          err?.message || "Não foi possível carregar o status do domínio de e-mail."
        );
      })
      .finally(() => {
        setEmailDomainLoading(false);
      });
  };

  useEffect(() => {
    refreshEmailDomainStatus();
  }, [isEditing, channel?.id, tipo]);

  const refreshWebsiteConfig = () => {
    if (!isEditing || !channel?.id || tipo !== CHANNEL_TYPES.WEBSITE_VIACAO) return;
    setWebsiteConfigLoading(true);
    getChannelWebsiteConfig(channel.id)
      .then((res) => {
        setWebsiteConfig(res || null);
      })
      .catch((err) => {
        console.error("Erro ao carregar configuração do website:", err);
        setWebsiteConfig(null);
      })
      .finally(() => {
        setWebsiteConfigLoading(false);
      });
  };

  useEffect(() => {
    refreshWebsiteConfig();
  }, [isEditing, channel?.id, tipo]);

  const refreshWebsiteDomainStatus = () => {
    if (!isEditing || !channel?.id || tipo !== CHANNEL_TYPES.WEBSITE_VIACAO) return;
    getChannelWebsiteDomainStatus(channel.id)
      .then((res) => setWebsiteDomainInfo(res || null))
      .catch(() => setWebsiteDomainInfo(null));
  };

  useEffect(() => {
    refreshWebsiteDomainStatus();
  }, [isEditing, channel?.id, tipo, websiteConfig?.customDomain]);

  useEffect(() => {
    if (!isEditing || !channel?.id || tipo !== CHANNEL_TYPES.WEBSITE_VIACAO) return;
    listChannelWebsiteAudit(channel.id)
      .then((rows) => setWebsiteAudit(Array.isArray(rows) ? rows : []))
      .catch(() => setWebsiteAudit([]));
  }, [isEditing, channel?.id, tipo, websiteConfigMessage]);

  const handleImportGupshupTemplatesClick = async () => {
    setImportTemplatesError(null);
    setImportTemplatesResult(null);

    const originId = formData.id || channel?.id;
    if (!originId) {
      setImportTemplatesError(
        "Salve o canal primeiro para gerar o ID da origem antes de importar templates."
      );
      return;
    }

    try {
      setIsImportingTemplates(true);
      const result = await importGupshupTemplates(originId);
      setImportTemplatesResult(result || {});
      if (!result?.warning) {
        loadChannelTemplates(originId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao importar templates da Gupshup:", error);
      setImportTemplatesError(
        error?.response?.data?.message ||
          error?.message ||
          "Erro ao importar templates da Gupshup."
      );
    } finally {
      setIsImportingTemplates(false);
    }
  };

  const renderCascade = (base, titulo) => {
    const { l1, l2, l3 } = getCascadeLevels(base);
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {titulo}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Tipo"
            value={formData[`${base}_type_id`] ?? ""}
            onChange={(e) => handleCascadeChange(base, 0, e.target.value)}
          >
            <MenuItem value="">—</MenuItem>
            {rootTypes.map((t) => (
              <MenuItem key={`${base}-root-${t.id}`} value={t.id}>
                {titleOf(t)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {l1.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 1"
              value={formData[`${base}_type1_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 1, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l1.map((s) => (
                <MenuItem key={`${base}-l1-${s.id}`} value={s.id}>
                  {titleOf(s)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l2.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 2"
              value={formData[`${base}_type2_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 2, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l2.map((u) => (
                <MenuItem key={`${base}-l2-${u.id}`} value={u.id}>
                  {titleOf(u)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l3.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 3"
              value={formData[`${base}_type3_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 3, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l3.map((v) => (
                <MenuItem key={`${base}-l3-${v.id}`} value={v.id}>
                  {titleOf(v)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </>
    );
  };

  // Descobre dinamicamente todas as bases *_type_id (exceto id_tipo, que é o roteamento padrão)
  const cascadeBases = useMemo(() => {
    const keys = Object.keys(formData || {});
    const basesAll = keys
      .filter((k) => k.endsWith("_type_id") && k !== "id_tipo")
      .map((k) => k.replace(/_type_id$/, ""));
    const basesUnique = Array.from(new Set(basesAll));

    // *** regra: ecom_* só aparece quando tipo === ECOMMERCE ***
    if (tipo !== CHANNEL_TYPES.ECOMMERCE) {
      return basesUnique.filter((b) => !ECOM_BASES.includes(b));
    }
    // quando é Ecommerce, mantém apenas as bases ecom, na ordem desejada
    return ECOM_BASES.filter((b) => basesUnique.includes(b));
  }, [formData, tipo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setClientSideError(null);

    // Trabalhamos sempre com uma cópia local para poder aplicar validações/ajustes
    // sem mutar o estado original de forma inesperada.
    let workingFormData = { ...formData };

    // Gera token automaticamente para canais de Telefonia apenas em canal novo, quando ainda não existir
    if (tipo === CHANNEL_TYPES.PABX && !workingFormData.token && !workingFormData.id) {
      const rand =
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
      workingFormData.token = `tel_${rand}`;
    }

    // ======== Regras específicas para canal de E-mail (tipo 35 / EMAIL_AWS_SES) ========
    if (tipo === CHANNEL_TYPES.EMAIL_AWS_SES) {
      const rawEmail = String(workingFormData.email || "").trim();
      const emailMatch = rawEmail.match(/^([^@\s]+)@([^@\s]+\.[^@\s]+)$/);

      if (!rawEmail) {
        setClientSideError('O campo "Endereço de e-mail" é obrigatório.');
        return;
      }

      if (!emailMatch) {
        setClientSideError(
          'Informe um endereço de e-mail válido, por exemplo "contato@seudominio.com".'
        );
        return;
      }

      const emailDomain = emailMatch[2].toLowerCase();
      const configuredDomain = String(workingFormData.email_domain || "")
        .trim()
        .toLowerCase();

      // domínios considerados "sem configuração extra" (shared)
      const DEFAULT_SHARED_DOMAIN = "xpro.me";
      const workspaceDomain = getWorkspaceDomain().toLowerCase();

      // Se o usuário não informou domínio próprio, só permitimos usar domínio compartilhado
      // (xpro.me) ou o domínio do workspace (workspace.xpro.me).
      if (
        !configuredDomain &&
        emailDomain !== DEFAULT_SHARED_DOMAIN &&
        emailDomain !== workspaceDomain
      ) {
        const workspaceExample = workspaceDomain || "seuworkspace.xpro.me";

        setClientSideError(
          `Para usar o domínio "@${emailDomain}", clique em "Usar domínio próprio" e preencha esse domínio no campo de domínio próprio. ` +
            `Se você ainda não configurou um domínio, use um endereço com o domínio padrão Xpro, por exemplo "contato@${workspaceExample}" ou "contato@xpro.me".`
        );
        return;
      }

      // Se o domínio próprio foi informado, o e-mail do canal deve usar exatamente esse domínio.
      if (configuredDomain && emailDomain !== configuredDomain) {
        setClientSideError(
          `O endereço de e-mail do canal deve usar o domínio "${configuredDomain}". ` +
            `Ajuste o campo "Endereço de e-mail" para algo como "contato@${configuredDomain}".`
        );
        return;
      }

      // Persistimos a versão normalizada na cópia local (garante consistência de caixa).
      workingFormData = {
        ...workingFormData,
        email: `${emailMatch[1]}@${emailDomain}`,
        email_domain: configuredDomain || workingFormData.email_domain,
      };
    }

    // obrigatórios comuns + Empresa (id_projeto) + id_tipo (padrão)
    const requiredChecks = [
      ...COMMON_FIELDS.filter((f) => f.required).map((f) => ({ name: f.name, label: f.label })),
      { name: "id_projeto", label: "Empresa" },
      { name: "id_tipo", label: "Tipo" },
      ...(FIELDS_BY_TYPE[tipo] || [])
        .filter((f) => f.required)
        .map((f) => ({ name: f.name, label: f.label })),
    ];
    for (const f of requiredChecks) {
      const v = workingFormData[f.name];
      if (isEmptyValue(v)) {
        setClientSideError(`O campo "${f.label}" é obrigatório.`);
        return;
      }
    }

    try {
      const ids = Array.isArray(workingFormData.idusuarios)
        ? workingFormData.idusuarios
        : [];
      // id_chatbot: usar ref (fonte de verdade do select); não confiar no spread de formData
      const idChatbotVal = idChatbotRef.current ?? workingFormData.id_chatbot;
      let idChatbotPayload = null;
      if (idChatbotVal != null && idChatbotVal !== "") {
        const num = Number(idChatbotVal);
        idChatbotPayload = Number.isNaN(num) ? idChatbotVal : num;
      }
      // id_agente: usar ref (fonte de verdade do select); não confiar no spread de formData que pode ter sido sobrescrito
      const idAgenteVal = idAgenteRef.current ?? formData.id_agente;
      // Suportar id numérico ou UUID (string); Number(uuid) = NaN e JSON.stringify(NaN) vira null
      let idAgentePayload = null;
      if (idAgenteVal != null && idAgenteVal !== "") {
        const num = Number(idAgenteVal);
        idAgentePayload = Number.isNaN(num) ? idAgenteVal : num;
      }
      const { id_agente: _omitIdAgente, id_chatbot: _omitIdChatbot, ...formDataRest } = workingFormData;
      const payload = {
        ...formDataRest,
        idusuario:
          idChatbotPayload != null
            ? idChatbotPayload
            : ids.length
              ? ids[0]
              : null,
        id_usuarios_padrao: ids,
        id_agente: idAgentePayload,
      };
      await onSubmit(payload);
    } catch (error) {
      const raw = error || {};
      const serverMessage =
        (typeof raw.message === "string" && raw.message) ||
        (typeof raw.data === "string" && raw.data) ||
        (typeof raw.error === "string" && raw.error) ||
        null;

      let friendly = serverMessage || "Erro ao salvar o canal.";

      if (typeof serverMessage === "string") {
        if (serverMessage.includes("Column 'id_projeto'")) {
          friendly =
            "Selecione uma Empresa para este canal. A empresa é obrigatória para salvar o canal.";
        } else if (serverMessage.includes("Column 'id_tipo'")) {
          friendly =
            "Selecione um Tipo padrão em \"Roteamento padrão\". Esse tipo é obrigatório para salvar o canal.";
        }
      }

      setErrorMessage(friendly);
    }
  };

  const allRequiredFilled = useMemo(() => {
    const baseChecks = [
      ...COMMON_FIELDS.filter((f) => f.required).map((f) => f.name),
      "id_projeto",
      "id_tipo",
    ];
    const typeChecks = (FIELDS_BY_TYPE[tipo] || [])
      .filter((f) => f.required)
      .map((f) => f.name);
    const all = [...baseChecks, ...typeChecks];
    return all.every((name) => {
      const value = formData[name];
      // Campos sensíveis: se já existe segredo salvo (has_*), consideramos preenchido mesmo com valor oculto
      if (
        (name === "tokenwhatsapp" ||
          name === "token_messenger" ||
          name === "token_instagram" ||
          name === "apikeygupshup" ||
          name === "senha" ||
          name === "ecom_api_token" ||
          name === "token") &&
        hasSecret(name)
      ) {
        return true;
      }
      return !isEmptyValue(value);
    });
  }, [formData, tipo]);

  const isEmailSesChannel = tipo === CHANNEL_TYPES.EMAIL_AWS_SES;
  const isWebsiteChannel = tipo === CHANNEL_TYPES.WEBSITE_VIACAO;
  const websitePreviewTheme = {
    primaryColor: websiteConfig?.themeTokens?.primaryColor || "#0F4C81",
    secondaryColor: websiteConfig?.themeTokens?.secondaryColor || "#F5A623",
    accentColor: websiteConfig?.themeTokens?.accentColor || "#2ECC71",
    textColor: websiteConfig?.themeTokens?.textColor || "#1F2937",
    backgroundColor: websiteConfig?.themeTokens?.backgroundColor || "#FFFFFF",
    fontFamily: websiteConfig?.themeTokens?.fontFamily || "Inter",
    borderRadius: websiteConfig?.themeTokens?.borderRadius || "8px",
  };
  const websitePreviewMenu = Array.isArray(websiteConfig?.menuItems)
    ? websiteConfig.menuItems
    : ["home", "passagens", "contato", "faq"];
  const hasCustomEmailDomain =
    isEmailSesChannel &&
    !isSharedXproDomain(formData.email_domain);
  const isTelephonyChannel = tipo === CHANNEL_TYPES.PABX;
  const isApifacilWhatsappChannel = tipo === CHANNEL_TYPES.WHATSAPP_QR;
  const moveWebsiteMenuItem = (index, direction) => {
    setWebsiteConfig((prev) => {
      const current = Array.isArray(prev?.menuItems) ? [...prev.menuItems] : [];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return prev;
      const temp = current[index];
      current[index] = current[nextIndex];
      current[nextIndex] = temp;
      return {
        ...(prev || {}),
        menuItems: current,
      };
    });
  };
  const formatAuditAction = (action) => {
    const key = String(action || "");
    if (key === "config_updated") return "Configuração atualizada";
    if (key === "temp_url_regenerated") return "URL temporária regenerada";
    if (key === "domain_setup_started") return "Configuração de domínio iniciada";
    return key || "Ação";
  };
  const buildAuditDiffSummary = (entry) => {
    const payload = entry?.payload || {};
    const before = payload?.before || null;
    const after = payload?.after || payload || null;
    if (!after) return "";
    const chunks = [];
    if (before && before.customDomain !== after.customDomain) {
      chunks.push(`domínio: ${before.customDomain || "vazio"} -> ${after.customDomain || "vazio"}`);
    }
    if (before && JSON.stringify(before.menuItems || []) !== JSON.stringify(after.menuItems || [])) {
      chunks.push("menu alterado");
    }
    if (before && JSON.stringify(before.themeTokens || {}) !== JSON.stringify(after.themeTokens || {})) {
      chunks.push("tema alterado");
    }
    if (before && JSON.stringify(before.mainImages || {}) !== JSON.stringify(after.mainImages || {})) {
      chunks.push("imagens do site alteradas");
    }
    if (!chunks.length && payload?.tempUrl) {
      chunks.push(`nova URL: ${payload.tempUrl}`);
    }
    if (!chunks.length && payload?.customDomain) {
      chunks.push(`domínio: ${payload.customDomain}`);
    }
    return chunks.join(" | ");
  };
  const filteredWebsiteAudit = useMemo(() => {
    const now = Date.now();
    const periodMs =
      websiteAuditPeriodFilter === "24h"
        ? 24 * 60 * 60 * 1000
        : websiteAuditPeriodFilter === "30d"
          ? 30 * 24 * 60 * 60 * 1000
          : 7 * 24 * 60 * 60 * 1000;
    return (websiteAudit || []).filter((entry) => {
      const actionOk =
        websiteAuditActionFilter === "all" ||
        String(entry?.action || "") === websiteAuditActionFilter;
      const createdMs = new Date(entry?.createdAt || 0).getTime();
      const periodOk = Number.isFinite(createdMs) && now - createdMs <= periodMs;
      return actionOk && periodOk;
    });
  }, [websiteAudit, websiteAuditActionFilter, websiteAuditPeriodFilter]);
  const toggleWebsiteAuditExpanded = (auditId) => {
    setExpandedWebsiteAuditIds((prev) => {
      const id = Number(auditId);
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const handleApifacilQrError = (err) => {
    const rawMessage =
      (typeof err === "string" && err) ||
      err?.message ||
      err?.error ||
      (Array.isArray(err?.messages) && err.messages[0]) ||
      err?.response?.data?.message ||
      err?.data?.message ||
      "";
    if (rawMessage && /qr code n.o dispon.vel/i.test(rawMessage)) {
      // Caso esperado: QR anterior expirou ou novo QR ainda não foi gerado.
      // Mostra aviso suave em vez de erro.
      setErrorMessage(null);
      setClientSideError(
        "O QR Code desta instância expirou ou ainda não foi gerado. Aguarde alguns segundos e clique em \"Mostrar QRCode\" novamente."
      );
      setApifacilQrExpiresAt(null);
      return;
    }
    const msg =
      rawMessage ||
      "Erro ao obter informações do WhatsApp QRCode. Tente novamente em instantes.";
    setErrorMessage(msg);
    setApifacilQrExpiresAt(null);
  };

  const refreshApifacilStatus = async () => {
    try {
      const originId = formData.id || channel?.id;
      if (!originId) {
        setApifacilStatus(null);
        setApifacilStatusHint(null);
        return;
      }
      const statusData = await getApifacilInstanceStatus(originId);
      setApifacilStatus(statusData || null);
      const statusBanco = statusData?.raw?.data?.status_banco || statusData?.raw?.data?.status;
      const temQrCode = statusData?.raw?.data?.tem_qrcode;

      if (statusBanco === "connected") {
        setApifacilStatusHint(
          "Instância conectada com sucesso. Este canal já pode enviar e receber mensagens."
        );
        // Quando conectado, não faz mais sentido manter o QRCode na tela.
        setApifacilQrData(null);
        setApifacilQrExpiresAt(null);
      } else if (statusBanco === "disconnected") {
        setApifacilStatusHint(
          "Instância desconectada. Gere um novo QRCode e faça login novamente no WhatsApp."
        );
      } else if (statusBanco === "inactive") {
        setApifacilStatusHint(
          "Instância criada, mas ainda não conectada. Gere o QRCode e leia com o WhatsApp."
        );
      } else if (temQrCode === false) {
        setApifacilStatusHint(
          "Ainda não há QRCode ativo para esta instância. Clique em \"Mostrar QRCode\" para gerar um novo código."
        );
      } else {
        setApifacilStatusHint(null);
      }
    } catch (err) {
      // Não exibe erro para o usuário; apenas limpa o status para evitar ruído.
      // eslint-disable-next-line no-console
      console.warn("Erro ao obter status da instância WhatsApp QRCode:", err);
      setApifacilStatus(null);
      setApifacilStatusHint(null);
    }
  };

  const computeApifacilWebhookUrl = () => {
    const company =
      typeof window !== "undefined"
        ? window.localStorage.getItem("company")
        : null;
    const qsCompany = company ? `?company=${encodeURIComponent(company)}` : "";
    // Webhook da API Fácil deve sempre apontar para o domínio público,
    // mesmo em ambiente de desenvolvimento.
    return `${PUBLIC_API_BASE}/webhooks/whatsapp/apifacil${qsCompany}`;
  };

  const refreshApifacilConfig = async () => {
    try {
      const originId = formData.id || channel?.id;
      if (!originId) return;
      // só faz sentido buscar config quando há instância vinculada
      const instanciaId = formData.numerowhatsapp || channel?.numerowhatsapp;
      if (!instanciaId) return;

      setApifacilConfigLoading(true);
      const res = await getApifacilInstanceConfig(originId);
      const cfg = res?.configuracao || res?.data?.configuracao || null;
      if (cfg && typeof cfg === "object") {
        const json = cfg.config_json && typeof cfg.config_json === "object" ? cfg.config_json : {};
        setApifacilConfig((prev) => ({
          ...prev,
          rejeitar_chamadas: cfg.rejeitar_chamadas ?? prev.rejeitar_chamadas,
        }));
      }
    } catch (e) {
      // silencioso — a tela continua com defaults
    } finally {
      setApifacilConfigLoading(false);
    }
  };

  const copyExampleText = async (text, setCopied) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  // Poll leve para atualizar o status visualmente enquanto o usuário está na tela de configuração
  useEffect(() => {
    if (!isApifacilWhatsappChannel) return undefined;
    if (!formData.numerowhatsapp && !channel?.numerowhatsapp) return undefined;

    let cancelled = false;
    let intervalId;

    const tick = async () => {
      if (cancelled) return;
      const status = await refreshApifacilStatus();
      const statusBanco =
        status?.raw?.data?.status_banco || status?.raw?.data?.status;
      // Se já estiver conectado, não faz sentido continuar fazendo chamadas.
      if (statusBanco === "connected" && !cancelled && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Atualiza imediatamente ao entrar
    tick();
    // Depois mantém atualizado a cada 12s enquanto não estiver conectado
    intervalId = setInterval(tick, 12000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isApifacilWhatsappChannel, formData.numerowhatsapp, channel?.numerowhatsapp]);

  // Carrega configuração da instância para preencher a UI (best-effort)
  useEffect(() => {
    if (!isApifacilWhatsappChannel) return;
    if (!formData.id && !channel?.id) return;
    if (!formData.numerowhatsapp && !channel?.numerowhatsapp) return;
    refreshApifacilConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApifacilWhatsappChannel, formData.id, channel?.id, formData.numerowhatsapp, channel?.numerowhatsapp]);

  return (
    <Paper elevation={3} sx={{ p: 3, width: "95%", mx: "auto" }} ref={topRef}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? "Editar Canal" : "Adicionar Canal"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {clientSideError && !errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning">{clientSideError}</Alert>
        </Box>
      )}

      {/* Step 1: Seleção do tipo (somente inclusão) */}
      {!isEditing && step === 1 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Selecione o tipo de canal
          </Typography>

          {/* FLEX WRAP com margem própria por item */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {CHANNEL_CARDS.map((card) => (
              <Box
                key={card.id}
                sx={{
                  flex: "1 1 280px",
                  minWidth: 280,
                  maxWidth: {
                    xs: "100%",
                    sm: "calc(50% - 24px)",
                    md: "calc(33.33% - 24px)",
                    lg: "calc(25% - 24px)",
                  },
                  mb: 5,
                  boxSizing: "border-box",
                }}
              >
                <TypeCard
                  card={card}
                  selected={tipo === card.id}
                  onClick={() => setTipo(card.id)}
                />
              </Box>
            ))}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => setStep(2)}
              disabled={tipo == null || isLoading}
            >
              Continuar
            </Button>
          </Stack>
        </>
      )}

      {/* Step 2: Parâmetros por tipo */}
      {(isEditing || (!isEditing && step === 2 && tipo != null)) && (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            {selectedCard?.icon?.({})}
            <Typography variant="subtitle1" fontWeight={700}>
              {selectedCard?.label ?? "Tipo não informado"}
            </Typography>
            {tipo != null && <Chip size="small" label={`Tipo #${tipo}`} />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedCard?.description}
          </Typography>

          <Grid container spacing={2.5}>
            {/* Campos comuns básicos: primeiro Nome do Canal */}
            <Grid item xs={12} sm={6}>
              <FieldControl
                field={COMMON_FIELDS.find((f) => f.name === "nome")}
                value={formData.nome}
                onChange={onChange}
              />
            </Grid>

            {/* Depois Empresa (projeto) */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Empresa"
                required
                value={formData.id_projeto ?? ""}
                onChange={(e) => onChange("id_projeto", asNumOrNull(e.target.value))}
                helperText="Selecione a empresa vinculada a este canal"
              >
                {loadingCompanies && <MenuItem value="">Carregando...</MenuItem>}
                {!loadingCompanies && <MenuItem value="">—</MenuItem>}
                {!loadingCompanies &&
                  companyList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {titleOf(c)}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            {/* Demais campos comuns (ex: status) */}
            {COMMON_FIELDS.filter((f) => f.name !== "nome").map((f) => (
              <Grid key={f.name} item xs={12} sm={f.multiline ? 12 : 6}>
                <FieldControl field={f} value={formData[f.name]} onChange={onChange} />
              </Grid>
            ))}

            {/* Roteamento padrão: Usuário & Tipo / Subtipos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Roteamento padrão
              </Typography>
            </Grid>

            {/* Usuário padrão */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Usuários Humanos Padrão"
                value={formData.idusuarios ?? []}
                disabled={hasChatbotRouting || hasAgentRouting}
                onChange={(e) => {
                  const val = e.target.value;
                  const ids = Array.isArray(val) ? val.map((v) => Number(v)) : [];
                  if (ids.length > 0) {
                    idChatbotRef.current = null;
                    idAgenteRef.current = null;
                    setFormData((prev) => ({
                      ...prev,
                      id_chatbot: null,
                      id_agente: null,
                      idusuarios: ids,
                      idusuario: ids[0],
                    }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      idusuarios: [],
                      idusuario: null,
                    }));
                  }
                }}
                helperText="Quem receberá por padrão (humanos). Só um destino ativo: humanos, chatbot ou agente de IA — os outros ficam desativados enquanto houver seleção."
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const ids = Array.isArray(selected) ? selected.map(Number) : [];
                    if (!ids.length) return "Nenhum usuário";
                    const names = (activeHumanUsers.length ? activeHumanUsers : allHumanUsers)
                      .filter((u) => ids.includes(Number(u.id)))
                      .map((u) => titleOf(u));
                    return names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
                  },
                }}
              >
                {loadingUsers && <MenuItem value="">Carregando...</MenuItem>}
                {!loadingUsers &&
                  (activeHumanUsers.length ? activeHumanUsers : allHumanUsers).map((u) => {
                    const checked = (formData.idusuarios || []).includes(Number(u.id));
                    return (
                      <MenuItem key={u.id} value={u.id}>
                        <Checkbox checked={checked} />
                        <ListItemText primary={titleOf(u)} secondary={u?.email || undefined} />
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>

            {/* Autocomplete em vez de Select: Select dentro deste formulário grande falhava ao aplicar valor (MUI + estado). */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="add-channel-routing-chatbot"
                fullWidth
                autoComplete="off"
                options={chatbotMenuOptions}
                loading={loadingUsers}
                disabled={loadingUsers || hasHumanRouting || hasAgentRouting}
                value={selectedChatbotOption}
                onChange={(e, newValue) => {
                  const next = newValue == null ? null : String(newValue.id);
                  idChatbotRef.current = next;
                  if (next != null && next !== "") {
                    idAgenteRef.current = null;
                    setFormData((prev) => ({
                      ...prev,
                      id_chatbot: next,
                      id_agente: null,
                      idusuarios: [],
                      idusuario: Number(next),
                    }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      id_chatbot: null,
                      idusuario: null,
                    }));
                  }
                }}
                isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                getOptionLabel={(o) => o?.name ?? o?.nome ?? `Chatbot #${o?.id ?? ""}`}
                noOptionsText={
                  loadingUsers
                    ? "Carregando usuários…"
                    : "Nenhum chatbot (usuário com ura=1) nesta lista"
                }
                ListboxProps={{ sx: { maxHeight: 320 } }}
                slotProps={{
                  popper: {
                    sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Passar por Chatbot"
                    helperText="Novas demandas vão primeiro ao fluxo deste chatbot. Desative escolhendo outro destino ou limpando a seleção. Exclusivo com humanos e agente de IA."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? (
                            <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Agente de IA: demandas do canal podem passar pelo agente (não vão direto ao usuário) */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Passar por agente de IA"
                disabled={hasHumanRouting || hasChatbotRouting}
                value={formData.id_agente ?? idAgenteRef.current ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  idAgenteRef.current = v === "" ? null : v;
                  if (v === "") {
                    setFormData((prev) => ({ ...prev, id_agente: null }));
                    return;
                  }
                  idChatbotRef.current = null;
                  setFormData((prev) => ({
                    ...prev,
                    id_agente: v,
                    id_chatbot: null,
                    idusuarios: [],
                    idusuario: null,
                  }));
                }}
                helperText="Demandas vão primeiro ao agente. Exclusivo com humanos e chatbot; escolha Nenhum para liberar os outros destinos."
                SelectProps={{
                  renderValue: (selected) => {
                    if (selected == null || selected === "") return "Nenhum";
                    const agent = agentsList.find((a) => String(a.id) === String(selected));
                    return agent ? (agent.name ?? agent.nome ?? `Agente #${agent.id}`) : "Nenhum";
                  },
                }}
              >
                <MenuItem value="">Nenhum</MenuItem>
                {agentsList
                  .filter((a) => a.status === true || a.status === "active" || a.enabled === true || a.is_active === true)
                  .map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>
                      {a.name ?? a.nome ?? `Agente #${a.id}`}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            {/* Tipo (nível 0) — linha própria abaixo do roteamento */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Tipo"
                required
                value={formData.id_tipo ?? ""}
                onChange={(e) => handleRootTypeChange(e.target.value)}
                helperText="Tipo padrão quando não vier explícito"
              >
                {loadingTypes && <MenuItem value="">Carregando...</MenuItem>}
                {!loadingTypes &&
                  rootTypes.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {titleOf(t)}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            {/* Subtipo 1 */}
            {level1.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Subtipo 1"
                  value={formData.id_tipo1 ?? ""}
                  onChange={(e) => handleLvl1Change(e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {level1.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {titleOf(s)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Subtipo 2 */}
            {level2.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Subtipo 2"
                  value={formData.id_tipo2 ?? ""}
                  onChange={(e) => handleLvl2Change(e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {level2.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {titleOf(u)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Subtipo 3 */}
            {level3.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Subtipo 3"
                  value={formData.id_tipo3 ?? ""}
                  onChange={(e) => handleLvl3Change(e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {level3.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {titleOf(v)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* ============================
                Guia de integração – Telefonia
                ============================ */}
            {isTelephonyChannel && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                </Grid>
                <Grid item xs={12} md={7}>
                  <Box
                    sx={(theme) => ({
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.mode === "light"
                        ? theme.palette.grey[50]
                        : theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                    })}
                  >
                    <Typography variant="overline" sx={{ letterSpacing: 1 }} color="primary">
                      Integração com telefonia (PABX)
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Como conectar sua central telefônica ao Xpro
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Configure os endpoints abaixo no seu PABX ou na sua central telefônica. Cada ligação recebida
                      irá abrir automaticamente a solicitação do cliente na tela do atendente, usando o canal de
                      Telefonia configurado aqui.
                    </Typography>

                  {(() => {
                    const workspaceSlug =
                      (typeof window !== "undefined"
                        ? window.localStorage.getItem("company")
                        : "") || "";
                    const apiBase =
                      typeof window !== "undefined" &&
                      "https://secure.xpro.me";
                    const companyHeader = workspaceSlug || "<workspace>";
                    const incomingPath = "/telephony/incoming";
                    const endPath = "/telephony/end";

                    const incomingUrl = `${apiBase}${incomingPath}?company=${companyHeader}`;
                    const endUrl = `${apiBase}${endPath}?company=${companyHeader}`;

                    const tokenValue = formData.token || "<TOKEN_DO_CANAL>";

                    const incomingBody = `{
  "id_chamada": "SEU_ID_DA_CHAMADA",
  "telefone": "+5511999998888",
  "data": "2026-01-01 12:00:00",
  "status": "ringing",
  "token": "${tokenValue}",
  "codigo_agente": "1001"
}`;

                    const endBody = `{
  "id_chamada": "SEU_ID_DA_CHAMADA",
  "token": "${tokenValue}",
  "tempo_espera": 10,
  "duracao": 180,
  "tempo_ura": 20,
  "abandonada": 0,
  "perdida": 0,
  "atendida": 1,
  "status": "completed"
}`;

                    return (
                      <Stack spacing={2}>
                        <Typography variant="subtitle2">
                          1. URLs da API
                        </Typography>
                        <List dense>
                          <ListItem sx={{ display: "block", py: 0.5 }}>
                            <Typography variant="body2">
                              <strong>Início da chamada</strong>: {incomingUrl}
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "block", py: 0.5 }}>
                            <Typography variant="body2">
                              <strong>Fim da chamada</strong>: {endUrl}
                            </Typography>
                          </ListItem>
                        </List>

                        <Typography variant="body2">
                          <strong>2. Headers obrigatórios</strong>
                        </Typography>
                        <List dense>
                          <ListItem sx={{ display: "block", py: 0.25 }}>
                            <Typography variant="body2">
                              <strong>company</strong>:{" "}
                              {companyHeader === "<workspace>" ? "preencha com o workspace do cliente" : companyHeader}
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "block", py: 0.25 }}>
                            <Typography variant="body2">
                              <strong>Content-Type</strong>: application/json
                            </Typography>
                          </ListItem>
                        </List>

                        <Typography variant="body2">
                          <strong>3. Parâmetros principais</strong>
                        </Typography>
                        <List dense>
                          <ListItem sx={{ display: "block", py: 0.25 }}>
                            <Typography variant="body2">
                              <strong>id_chamada</strong>: ID único da chamada na sua central telefônica (PABX).
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "block", py: 0.25 }}>
                            <Typography variant="body2">
                              <strong>telefone</strong>: número do cliente (DDD + número).
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "block", py: 0.25 }}>
                            <Typography variant="body2">
                              <strong>token</strong>: token deste canal (campo abaixo).
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ display: "block", py: 0.25 }}>
                            <Typography variant="body2">
                              <strong>codigo_agente</strong> ou <strong>emailusuario</strong>: identificador do
                              atendente para abrir a aba na tela dele.
                            </Typography>
                          </ListItem>
                        </List>

                        <Typography variant="body2">
                          <strong>4. Exemplos completos (para enviar ao PABX)</strong>
                        </Typography>
                        <Stack spacing={1}>
                          <Box
                            sx={(theme) => ({
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: theme.palette.background.default,
                              border: "1px dashed",
                              borderColor: "divider",
                            })}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle2">Início da chamada – URL</Typography>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={copiedIncomingUrl ? <CheckIcon /> : <ContentCopyIcon />}
                                onClick={() => copyExampleText(incomingUrl, setCopiedIncomingUrl)}
                              >
                                {copiedIncomingUrl ? "Copiado" : "Copiar URL"}
                              </Button>
                            </Stack>
                            <Typography
                              component="pre"
                              variant="caption"
                              sx={{ mb: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                            >
                              {incomingUrl}
                            </Typography>

                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                              <Typography variant="subtitle2">Início da chamada – Body (JSON)</Typography>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={copiedIncomingBody ? <CheckIcon /> : <ContentCopyIcon />}
                                onClick={() => copyExampleText(incomingBody, setCopiedIncomingBody)}
                              >
                                {copiedIncomingBody ? "Copiado" : "Copiar body"}
                              </Button>
                            </Stack>
                            <Typography
                              component="pre"
                              variant="caption"
                              sx={{ mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                            >
                              {incomingBody}
                            </Typography>
                          </Box>

                          <Box
                            sx={(theme) => ({
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: theme.palette.background.default,
                              border: "1px dashed",
                              borderColor: "divider",
                            })}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle2">Fim da chamada – URL</Typography>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={copiedEndUrl ? <CheckIcon /> : <ContentCopyIcon />}
                                onClick={() => copyExampleText(endUrl, setCopiedEndUrl)}
                              >
                                {copiedEndUrl ? "Copiado" : "Copiar URL"}
                              </Button>
                            </Stack>
                            <Typography
                              component="pre"
                              variant="caption"
                              sx={{ mb: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                            >
                              {endUrl}
                            </Typography>

                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                              <Typography variant="subtitle2">Fim da chamada – Body (JSON)</Typography>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={copiedEndBody ? <CheckIcon /> : <ContentCopyIcon />}
                                onClick={() => copyExampleText(endBody, setCopiedEndBody)}
                              >
                                {copiedEndBody ? "Copiado" : "Copiar body"}
                              </Button>
                            </Stack>
                            <Typography
                              component="pre"
                              variant="caption"
                              sx={{ mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                            >
                              {endBody}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    );
                  })()}
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.mode === "light"
                        ? theme.palette.grey[50]
                        : theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: "divider",
                    })}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Dados rápidos para copiar
                    </Typography>
                    <Stack spacing={1.5}>
                      <FieldControl
                        field={{
                          name: "company_helper",
                          label: "Workspace (company)",
                          type: "readonly",
                        }}
                        value={
                          (typeof window !== "undefined"
                            ? window.localStorage.getItem("company")
                            : "") || ""
                        }
                        onChange={() => {}}
                      />
                      <FieldControl
                        field={{
                          name: "token",
                          label: "Token de segurança deste canal",
                          type: "readonly",
                        }}
                        value={formData.token || ""}
                        onChange={() => {}}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                      Dica: envie o workspace, o token deste canal e os exemplos de requisição ao time responsável
                      pelo PABX ou pela central telefônica. Eles conseguem fazer toda a configuração apenas com essas informações.
                    </Typography>

                    <Divider sx={{ my: 2.5 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        Testar integração
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: "block" }}>
                        Simule uma chamada recebida e depois o encerramento para ver o fluxo no Xpro (solicitação aberta e notificação de chamada). A notificação abre em qualquer tela.
                      </Typography>
                    </Box>

                    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1.5, letterSpacing: 1 }}>
                      Parâmetros da simulação (opcional)
                    </Typography>

                    <Box
                      sx={(theme) => ({
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: theme.palette.mode === "light" ? theme.palette.grey[50] : theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        mb: 2,
                      })}
                    >
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: "block", mb: 1.5 }}>
                        Chamada recebida
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                              size="small"
                              fullWidth
                              label="ID da chamada"
                              value={simIdChamada}
                              onChange={(e) => setSimIdChamada(e.target.value)}
                              helperText="Único por chamada"
                              slotProps={{ htmlInput: { "aria-label": "ID da chamada (mesmo para recebida e encerrar)" } }}
                            />
                            <Button
                              type="button"
                              size="small"
                              variant="outlined"
                              onClick={() => setSimIdChamada(`test-${Date.now()}`)}
                              sx={{ mt: 0.5, minWidth: 92, flexShrink: 0 }}
                            >
                              Gerar novo
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Telefone (origem)"
                            value={simTelefone}
                            onChange={(e) => setSimTelefone(e.target.value)}
                            placeholder="5511999998888"
                            helperText="Nº do cliente"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            freeSolo
                            size="small"
                            options={activeUsers}
                            loading={loadingUsers}
                            inputValue={
                              simAtendenteValue ||
                              (() => {
                                const s = userData || meData;
                                return String(
                                  s?.id_interno ??
                                    s?.codigo_agente ??
                                    s?.email ??
                                    ""
                                );
                              })()
                            }
                            onInputChange={(_, inputValue) => setSimAtendenteValue(inputValue)}
                            onChange={(_, newValue) => {
                              if (typeof newValue === "object" && newValue != null) {
                                setSimAtendenteValue(newValue.email || String(newValue.id_interno ?? newValue.codigo_agente ?? ""));
                              } else if (typeof newValue === "string") {
                                setSimAtendenteValue(newValue);
                              } else {
                                setSimAtendenteValue("");
                              }
                            }}
                            getOptionLabel={(opt) => {
                              if (typeof opt === "string") return opt;
                              const nome = opt.nome || opt.name || opt.email || "";
                              const email = opt.email ? `email: ${opt.email}` : "";
                              const idInt = opt.id_interno != null ? `id_interno: ${opt.id_interno}` : "";
                              const codigo = opt.codigo_agente != null ? `codigo_agente: ${opt.codigo_agente}` : "";
                              const extra = [email, idInt, codigo].filter(Boolean).join(" · ");
                              return extra ? `${nome} (${extra})` : nome || "—";
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Quem recebe a notificação"
                                placeholder="E-mail ou id interno"
                                helperText="emailusuario ou codigo_agente"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box
                      sx={(theme) => ({
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: theme.palette.mode === "light" ? theme.palette.grey[50] : theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        mb: 2,
                      })}
                    >
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: "block", mb: 1.5 }}>
                        Chamada terminada
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label="Tempo espera (s)"
                            value={simTempoEspera}
                            onChange={(e) => setSimTempoEspera(e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label="Duração (s)"
                            value={simDuracao}
                            onChange={(e) => setSimDuracao(e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label="Atendida"
                            value={simAtendida}
                            onChange={(e) => setSimAtendida(e.target.value)}
                          >
                            <MenuItem value="1">Sim</MenuItem>
                            <MenuItem value="0">Não</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box
                      sx={(theme) => ({
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        alignItems: "stretch",
                        mt: 2,
                        pt: 2,
                        borderTop: "1px solid",
                        borderColor: theme.palette.divider,
                      })}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        size="medium"
                        color="primary"
                        startIcon={telephonySimulateIncomingLoading ? <CircularProgress size={18} color="inherit" /> : <CallIcon />}
                        disabled={telephonySimulateIncomingLoading || telephonySimulateEndLoading}
                        onClick={handleSimulateIncomingCall}
                        sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 200, py: 1.25 }}
                      >
                        {telephonySimulateIncomingLoading ? "Enviando…" : "Simular chamada recebida"}
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        startIcon={telephonySimulateEndLoading ? <CircularProgress size={18} color="inherit" /> : <CallEndIcon />}
                        disabled={telephonySimulateEndLoading || telephonySimulateIncomingLoading}
                        onClick={handleSimulateEndCall}
                        sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 200, py: 1.25 }}
                      >
                        {telephonySimulateEndLoading ? "Enviando…" : "Simular chamada terminada"}
                      </Button>
                    </Box>
                    {telephonySimulateMessage && (
                      <Alert
                        severity={telephonySimulateSeverity}
                        onClose={() => setTelephonySimulateMessage(null)}
                        sx={{ mt: 1.5 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                          <span>{telephonySimulateMessage}</span>
                          {String(telephonySimulateMessage || "").includes("Ligação já registrada") && (
                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              sx={{ textTransform: "none", minWidth: "auto", p: 0.25, ml: 0.5 }}
                              onClick={() => {
                                setSimIdChamada(`test-${Date.now()}`);
                                setTelephonySimulateMessage(null);
                              }}
                            >
                              Gerar novo ID
                            </Button>
                          )}
                        </Box>
                      </Alert>
                    )}
                  </Box>
                </Grid>
              </>
            )}

            {/* ============ Cascatas dinâmicas para TODAS as bases *_type_id ============ */}
            {cascadeBases.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Mapeamentos por Tipo
                  </Typography>
                </Grid>

                {cascadeBases.map((base) =>
                  renderCascade(base, BASE_TITLES[base] ?? toTitle(base))
                )}
              </>
            )}

            {/* (Ficam aqui quaisquer campos específicos dos outros tipos) */}
            {/* Bloco especial: WhatsApp QRCode */}
            {isApifacilWhatsappChannel && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                </Grid>
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Uso com cautela (WhatsApp QRCode)
                    </Typography>
                    <Typography variant="body2">
                      Este canal depende de uma sessão conectada via QR Code, que pode{" "}
                      <strong>desconectar</strong> por condições fora do controle da plataforma
                      (internet do celular/PC, modo economia de bateria, suspensão do navegador,
                      limpeza de cookies/cache, VPN/proxy, mudanças nos serviços do WhatsApp).
                      Recomendado para <strong>fluxos pequenos</strong> e operações que tolerem
                      instabilidade.
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Boas práticas:</strong>
                      </Typography>
                      <List dense sx={{ listStyleType: "disc", pl: 3, my: 0 }}>
                        <ListItem sx={{ display: "list-item", py: 0 }}>
                          <Typography variant="body2">
                            Mantenha o celular com{" "}
                            <strong>internet estável</strong> e desative economia de bateria/dados
                            para o WhatsApp.
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ display: "list-item", py: 0 }}>
                          <Typography variant="body2">
                            Evite <strong>VPN/Proxy</strong> e redes com bloqueios a
                            <strong> web.whatsapp.com</strong>.
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ display: "list-item", py: 0 }}>
                          <Typography variant="body2">
                            Use navegador atualizado e evite extensões que limpam cookies/cache ou
                            “hibernam” abas.
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ display: "list-item", py: 0 }}>
                          <Typography variant="body2">
                            Se desconectar, gere um novo QRCode e reconecte.
                          </Typography>
                        </ListItem>
                      </List>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Não nos responsabilizamos por instabilidades, quedas de sessão, bloqueios
                      ou limitações impostas pelo WhatsApp/ambiente do cliente.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Box
                    sx={(theme) => ({
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "light"
                          ? theme.palette.grey[50]
                          : theme.palette.background.paper,
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="overline" sx={{ letterSpacing: 1 }} color="primary">
                        Integração WhatsApp QRCode
                      </Typography>
                      {apifacilStatus?.hasInstance && (
                        <Chip
                          size="small"
                          label={
                            (() => {
                              const statusBanco =
                                apifacilStatus?.raw?.data?.status_banco ||
                                apifacilStatus?.raw?.data?.status;
                              if (!statusBanco) return "Instância configurada";
                              if (statusBanco === "connected") return "Instância: ONLINE";
                              if (statusBanco === "disconnected")
                                return "Instância: DESCONECTADA";
                              if (statusBanco === "inactive")
                                return "Instância: INATIVA";
                              return `Instância: ${String(statusBanco).toUpperCase()}`;
                            })()
                          }
                          color={
                            (() => {
                              const statusBanco =
                                apifacilStatus?.raw?.data?.status_banco ||
                                apifacilStatus?.raw?.data?.status;
                              if (statusBanco === "connected") return "success";
                              if (statusBanco === "inactive") return "default";
                              if (statusBanco === "disconnected") return "warning";
                              return "default";
                            })()
                          }
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Stack>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Criar instância e conectar número
                    </Typography>
                    {apifacilStatusHint && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {apifacilStatusHint}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Crie uma instância e gere o QRCode para conectar o número. O instancia_id será salvo neste canal
                      e usado para enviar/receber mensagens.
                    </Typography>

                    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Configuração (API Fácil)
                        </Typography>
                        {apifacilConfigLoading && <CircularProgress size={16} />}
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        O Xpro aplica automaticamente a configuração recomendada de webhooks para receber status das
                        mensagens. Aqui você pode escolher apenas se deseja <b>rejeitar chamadas</b>.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(apifacilConfig.rejeitar_chamadas)}
                                onChange={(e) =>
                                  setApifacilConfig((prev) => ({
                                    ...prev,
                                    rejeitar_chamadas: e.target.checked,
                                  }))
                                }
                              />
                            }
                            label="Rejeitar chamadas automaticamente"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                            <Button
                              variant="outlined"
                              disabled={apifacilConfigLoading || apifacilLoading || !(formData.id || channel?.id)}
                              onClick={async () => {
                                const originId = formData.id || channel?.id;
                                if (!originId) return;
                                setApifacilConfigMessage(null);
                                setApifacilConfigSeverity("info");
                                setApifacilConfigLoading(true);
                                try {
                                  const webhookUrl = computeApifacilWebhookUrl();
                                  const payload = {
                                    webhook_url: webhookUrl,
                                    webhook_status: webhookUrl,
                                    webhook_grupo: webhookUrl,
                                    webhook_ativo: APIFACIL_DEFAULTS.webhook_ativo,
                                    ativar_notificacao_instancia: APIFACIL_DEFAULTS.ativar_notificacao_instancia,
                                    ativar_atualizacoes_interacao: APIFACIL_DEFAULTS.ativar_atualizacoes_interacao,
                                    rejeitar_chamadas: Boolean(apifacilConfig.rejeitar_chamadas),
                                    ativar_delay_envio_resposta: APIFACIL_DEFAULTS.ativar_delay_envio_resposta,
                                    tipos_envio: APIFACIL_DEFAULTS.tipos_envio,
                                  };
                                  await updateApifacilInstanceConfig(originId, payload);
                                  setApifacilConfigSeverity("success");
                                  setApifacilConfigMessage("Configurações atualizadas com sucesso.");
                                  refreshApifacilConfig();
                                } catch (err) {
                                  const msg =
                                    err?.message ||
                                    err?.response?.data?.message ||
                                    "Não foi possível atualizar as configurações. Verifique a instância e tente novamente.";
                                  setApifacilConfigSeverity("error");
                                  setApifacilConfigMessage(msg);
                                } finally {
                                  setApifacilConfigLoading(false);
                                }
                              }}
                            >
                              {apifacilConfigLoading ? (
                                <>
                                  <CircularProgress size={18} sx={{ mr: 1 }} /> Atualizando...
                                </>
                              ) : (
                                "Atualizar configurações"
                              )}
                            </Button>
                          </Stack>

                          {apifacilConfigMessage && (
                            <Box sx={{ mt: 1 }}>
                              <Alert severity={apifacilConfigSeverity}>{apifacilConfigMessage}</Alert>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Card>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={
                          apifacilLoading ||
                          !formData.nome ||
                          !(formData.id || channel?.id) ||
                          !!(formData.numerowhatsapp || channel?.numerowhatsapp)
                        }
                        onClick={async () => {
                          setApifacilQrData(null);
                          setApifacilLoading(true);
                          setErrorMessage(null);
                          try {
                            const originId = formData.id || channel?.id;
                            if (!originId) {
                              setErrorMessage(
                                "Salve o canal primeiro para gerar o ID antes de criar a instância."
                              );
                              return;
                            }
                            const company =
                              typeof window !== "undefined"
                                ? window.localStorage.getItem("company")
                                : null;
                            const qsCompany = company
                              ? `?company=${encodeURIComponent(company)}`
                              : "";
                            // Webhook da API Fácil deve sempre apontar para o domínio público,
                            // mesmo em ambiente de desenvolvimento.
                            const apiBase = "https://secure.xpro.me";
                            const webhookUrl = `${apiBase}/webhooks/whatsapp/apifacil${qsCompany}`;
                            const result = await createApifacilInstance(originId, {
                              nome: formData.nome,
                              webhook_url: webhookUrl,
                              webhook_status: webhookUrl,
                              webhook_grupo: webhookUrl,
                              rejeitar_chamadas: apifacilConfig.rejeitar_chamadas,
                              webhook_ativo: APIFACIL_DEFAULTS.webhook_ativo,
                              ativar_notificacao_instancia: APIFACIL_DEFAULTS.ativar_notificacao_instancia,
                              ativar_atualizacoes_interacao: APIFACIL_DEFAULTS.ativar_atualizacoes_interacao,
                              ativar_delay_envio_resposta: APIFACIL_DEFAULTS.ativar_delay_envio_resposta,
                              tipos_envio: APIFACIL_DEFAULTS.tipos_envio,
                            });
                            const instanciaId =
                              result?.data?.instancia?.id ||
                              result?.data?.instancia_id ||
                              result?.data?.instanciaId ||
                              result?.instancia_id ||
                              null;
                            if (instanciaId) {
                              setFormData((prev) => ({
                                ...prev,
                                numerowhatsapp: String(instanciaId),
                              }));
                              // Atualiza status após criação+start automáticos
                              refreshApifacilStatus();
                              // Atualiza config local (backend já tentou aplicar config na criação)
                              refreshApifacilConfig();
                              // Tenta buscar o QRCode logo após criar a instância.
                              try {
                                const qrData = await getApifacilInstanceQrCode(originId);
                                setApifacilQrData(qrData?.data || qrData);
                                // QRCode da API Fácil expira em ~20 segundos
                                setApifacilQrExpiresAt(Date.now() + 20_000);
                              } catch (qrErr) {
                                // Trata caso específico de "QR Code não disponível" de forma amigável.
                                handleApifacilQrError(qrErr);
                              }
                            }
                            setApifacilQrData(null);
                          } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error("Erro ao criar instância WhatsApp QRCode:", err);
                            const msg =
                              err?.message ||
                              err?.response?.data?.message ||
                              "Erro ao criar instância do WhatsApp QRCode.";
                            setErrorMessage(msg);
                          } finally {
                            setApifacilLoading(false);
                          }
                        }}
                      >
                        {apifacilLoading ? (
                          <>
                            <CircularProgress size={18} sx={{ mr: 1 }} /> Criando instância...
                          </>
                        ) : (
                          "Criar instância"
                        )}
                      </Button>

                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={
                          apifacilLoading ||
                          !formData.id ||
                          !formData.numerowhatsapp ||
                          apifacilStatus?.raw?.data?.status === "connected"
                        }
                        onClick={async () => {
                          setApifacilLoading(true);
                          setErrorMessage(null);
                          try {
                            const originId = formData.id || channel?.id;
                            if (!originId) {
                              setErrorMessage(
                                "Salve o canal primeiro para gerar o ID antes de buscar o QRCode."
                              );
                              return;
                            }
                            // Antes de exibir o QRCode, garantimos que o webhook esteja configurado/ativo
                            // para que as atualizações (Respondida/Lida etc.) comecem a chegar assim que conectar.
                            try {
                              const webhookUrl = computeApifacilWebhookUrl();
                              const payload = {
                                webhook_url: webhookUrl,
                                webhook_status: webhookUrl,
                                webhook_grupo: webhookUrl,
                                webhook_ativo: APIFACIL_DEFAULTS.webhook_ativo,
                                ativar_notificacao_instancia: APIFACIL_DEFAULTS.ativar_notificacao_instancia,
                                ativar_atualizacoes_interacao: APIFACIL_DEFAULTS.ativar_atualizacoes_interacao,
                                rejeitar_chamadas: Boolean(apifacilConfig.rejeitar_chamadas),
                                ativar_delay_envio_resposta: APIFACIL_DEFAULTS.ativar_delay_envio_resposta,
                                tipos_envio: APIFACIL_DEFAULTS.tipos_envio,
                              };
                              await updateApifacilInstanceConfig(originId, payload);
                              refreshApifacilConfig();
                            } catch (cfgErr) {
                              // Não bloqueia o QRCode; apenas informa que pode não receber updates.
                              setApifacilConfigSeverity("warning");
                              setApifacilConfigMessage(
                                "Não foi possível aplicar as configurações de webhook automaticamente. Você ainda pode escanear o QRCode, mas talvez não receba atualizações de status até clicar em “Atualizar configurações”."
                              );
                            }
                            const data = await getApifacilInstanceQrCode(originId);
                            setApifacilQrData(data?.data || data);
                            // Reinicia contador de expiração do QRCode
                            setApifacilQrExpiresAt(Date.now() + 20_000);
                            refreshApifacilStatus();
                          } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error("Erro ao obter QRCode WhatsApp QRCode:", err);
                            handleApifacilQrError(err);
                          } finally {
                            setApifacilLoading(false);
                          }
                        }}
                      >
                        {apifacilLoading ? (
                          <>
                            <CircularProgress size={18} sx={{ mr: 1 }} /> Buscando QRCode...
                          </>
                        ) : (
                          "Mostrar QRCode"
                        )}
                      </Button>
                    </Stack>

                    {apifacilQrData?.qrcode && (
                      <Box sx={{ mt: 3, textAlign: "center" }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Escaneie o QRCode abaixo no WhatsApp
                        </Typography>
                        <Box
                          sx={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <Box
                            component="img"
                            src={apifacilQrData.qrcode}
                            alt="QR Code WhatsApp"
                            sx={{
                              maxWidth: 260,
                              maxHeight: 260,
                              width: "100%",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              filter:
                                apifacilQrExpiresAt && Date.now() > apifacilQrExpiresAt
                                  ? "grayscale(1) opacity(0.4)"
                                  : "none",
                              transition: "filter 0.2s ease",
                            }}
                          />
                          {apifacilQrExpiresAt && Date.now() <= apifacilQrExpiresAt && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                bgcolor: "rgba(0,0,0,0.7)",
                                color: "#fff",
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: 12,
                              }}
                            >
                              {`Expira em ${Math.max(
                                0,
                                Math.ceil((apifacilQrExpiresAt - Date.now()) / 1000)
                              )}s`}
                            </Box>
                          )}
                          {(!apifacilQrExpiresAt || Date.now() > apifacilQrExpiresAt) && (
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0,0,0,0.55)",
                                borderRadius: 2,
                              }}
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={async () => {
                                  if (!formData.id && !channel?.id) return;
                                  setApifacilLoading(true);
                                  setErrorMessage(null);
                                  setClientSideError(null);
                                  try {
                                    const originId = formData.id || channel?.id;
                                    const data = await getApifacilInstanceQrCode(originId);
                                    setApifacilQrData(data?.data || data);
                                    setApifacilQrExpiresAt(Date.now() + 20_000);
                                    refreshApifacilStatus();
                                  } catch (err) {
                                    // eslint-disable-next-line no-console
                                    console.error(
                                      "Erro ao renovar QRCode WhatsApp QRCode:",
                                      err
                                    );
                                    handleApifacilQrError(err);
                                  } finally {
                                    setApifacilLoading(false);
                                  }
                                }}
                              >
                                Gerar novo QRCode
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Passos rápidos:</strong>
                  </Typography>
                  <List dense>
                    <ListItem sx={{ display: "list-item" }}>
                      <Typography variant="body2">
                        1. Salve o canal.
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                      <Typography variant="body2">
                        2. Clique em <strong>Criar instância</strong> para gerar uma instância
                        vinculada a este canal.
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }}>
                      <Typography variant="body2">
                        3. Após a criação, clique em <strong>Mostrar QRCode</strong> e escaneie com o WhatsApp
                        do número desejado.
                      </Typography>
                    </ListItem>
                  </List>
                </Grid>
              </>
            )}

            {!isTelephonyChannel && (FIELDS_BY_TYPE[tipo] || []).length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Configurações específicas
                </Typography>
              </Grid>
            )}

            {(FIELDS_BY_TYPE[tipo] || []).map((f) => {
              if (f.name === "id_projeto") return null;

              // Em canais Meta, o ID da página é escolhido apenas pelo select;
              // o campo de texto cru é ocultado para não confundir o usuário.
              if (
                (tipo === CHANNEL_TYPES.FB_MESSENGER ||
                  tipo === CHANNEL_TYPES.IG_MESSENGER) &&
                f.name === "id_pagina_facebook"
              ) {
                return null;
              }

              // Para canais Meta, escondemos o campo de token e mostramos apenas o fluxo de conexão/listagem
              if (
                (tipo === CHANNEL_TYPES.FB_MESSENGER && f.name === "token_messenger") ||
                (tipo === CHANNEL_TYPES.IG_MESSENGER && f.name === "token_instagram")
              ) {
                const tokenValue = metaAccessToken || formData[f.name] || "";

                const handleConnectMeta = async () => {
                  setMetaOauthError(null);
                  try {
                    setMetaConnecting(true);
                    const channelKey =
                      tipo === CHANNEL_TYPES.FB_MESSENGER
                        ? "facebook_messenger"
                        : tipo === CHANNEL_TYPES.IG_MESSENGER
                          ? "instagram_messenger"
                          : "generic";
                    const url = await getMetaOauthUrl(channelKey);
                    if (!url) {
                      setMetaOauthError("Não foi possível obter a URL de autenticação da Meta.");
                      return;
                    }
                    const width = 600;
                    const height = 700;
                    const left = window.screenX + (window.outerWidth - width) / 2;
                    const top = window.screenY + (window.outerHeight - height) / 2;
                    window.open(
                      url,
                      "meta_oauth",
                      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
                    );
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error("Erro ao iniciar OAuth da Meta:", err);
                    const msg =
                      err?.message ||
                      err?.response?.data?.message ||
                      "Não foi possível iniciar a autenticação com a Meta.";
                    setMetaOauthError(msg);
                  } finally {
                    setMetaConnecting(false);
                  }
                };

                return (
                  <Grid key={f.name} item xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Primeiro conecte sua conta Meta para liberar o token de acesso.
                        Em seguida, vamos listar automaticamente as páginas/contas disponíveis
                        para você escolher neste canal.
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleConnectMeta}
                          disabled={metaConnecting || isLoading}
                        >
                          {metaConnecting ? "Conectando..." : "Conectar com Meta"}
                        </Button>
                      </Box>
                      {metaOauthError && (
                        <Typography variant="caption" color="error">
                          {metaOauthError}
                        </Typography>
                      )}
                      {metaPagesError && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="error" display="block" sx={{ mb: 0.5 }}>
                            {metaPagesError}
                          </Typography>

                          {/* Passo a passo guiado para corrigir, separado por tipo de canal */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            Como resolver:
                          </Typography>

                          {tipo === CHANNEL_TYPES.IG_MESSENGER ? (
                            <List dense sx={{ pl: 2 }}>
                              <ListItem sx={{ py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  1. Confirme se você entrou com a conta Meta que é{" "}
                                  <strong>administradora</strong> da Página e da conta de Instagram que deseja usar.
                                </Typography>
                              </ListItem>
                              <ListItem sx={{ py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  2. Verifique se o Instagram é uma conta <strong>Profissional (Empresa ou Criador)</strong> e se está
                                  conectado a uma Página do Facebook.{" "}
                                  <Link
                                    href="https://www.facebook.com/business/help/898752960195806"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Ver passo a passo da Meta
                                  </Link>
                                  .
                                </Typography>
                              </ListItem>
                              <ListItem sx={{ py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  3. No fluxo de permissões da Meta, marque a Página/conta correta para este aplicativo e permita o
                                  acesso às mensagens.{" "}
                                  <Link
                                    href="https://developers.facebook.com/docs/messenger-platform/instagram/get-started/"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Ver requisitos da API de mensagens do Instagram
                                  </Link>
                                  .
                                </Typography>
                              </ListItem>
                            </List>
                          ) : (
                            <List dense sx={{ pl: 2 }}>
                              <ListItem sx={{ py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  1. Confirme se você entrou com a conta Meta que é{" "}
                                  <strong>administradora</strong> da Página que deseja usar.
                                </Typography>
                              </ListItem>
                              <ListItem sx={{ py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  2. Verifique se a Página está <strong>publicada</strong> e com o Messenger ativo.
                                </Typography>
                              </ListItem>
                              <ListItem sx={{ py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">
                                  3. No fluxo de permissões da Meta, selecione essa Página para este aplicativo e permita o acesso às
                                  mensagens.{" "}
                                  <Link
                                    href="https://www.facebook.com/business/help/1101781386943864"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Ver mais sobre acesso e funções na Página
                                  </Link>
                                  .
                                </Typography>
                              </ListItem>
                            </List>
                          )}
                        </Box>
                      )}
                      {(metaPages.length > 0 || formData.id_pagina_facebook) && (
                        <Box sx={{ mt: 1 }}>
                          <TextField
                            select
                            fullWidth
                            label={
                              tipo === CHANNEL_TYPES.FB_MESSENGER
                                ? "Selecione a Página do Facebook"
                                : "Selecione a conta de Instagram (via Página)"
                            }
                            value={formData.id_pagina_facebook ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const page = value
                                ? metaPages.find((p) => {
                                    const id =
                                      tipo === CHANNEL_TYPES.IG_MESSENGER
                                        ? p.instagramId
                                        : p.pageId;
                                    return id && String(id) === String(value);
                                  })
                                : null;
                              setFormData((prev) => {
                                const next = { ...prev, id_pagina_facebook: value };
                                if (tipo === CHANNEL_TYPES.IG_MESSENGER)
                                  next.token_instagram = page?.pageAccessToken ?? "";
                                else if (tipo === CHANNEL_TYPES.FB_MESSENGER)
                                  next.token_messenger = page?.pageAccessToken ?? "";
                                return next;
                              });
                            }}
                            helperText={
                              tipo === CHANNEL_TYPES.FB_MESSENGER
                                ? "Usaremos o ID da página selecionada para receber mensagens e enviar respostas."
                                : "Usaremos a conta de Instagram (Instagram Business ID) associada à página selecionada para este canal."
                            }
                          >
                            <MenuItem value="">—</MenuItem>
                            {/* Se existir uma página já salva que não esteja na lista atual da Meta,
                                ainda assim a exibimos como opção para que o usuário veja o que está configurado. */}
                            {formData.id_pagina_facebook &&
                              !metaPages.some((p) => {
                                const idForChannel =
                                  tipo === CHANNEL_TYPES.IG_MESSENGER
                                    ? p.instagramId
                                    : p.pageId;
                                return (
                                  idForChannel &&
                                  String(idForChannel) ===
                                    String(formData.id_pagina_facebook)
                                );
                              }) && (
                                <MenuItem value={formData.id_pagina_facebook}>
                                  {`Página atual (ID ${formData.id_pagina_facebook})`}
                                </MenuItem>
                              )}
                            {metaPages
                              .filter((p) =>
                                tipo === CHANNEL_TYPES.IG_MESSENGER
                                  ? p.hasInstagram && p.instagramId
                                  : true
                              )
                              .map((p) => (
                                <MenuItem
                                  key={
                                    tipo === CHANNEL_TYPES.IG_MESSENGER
                                      ? p.instagramId || p.pageId
                                      : p.pageId
                                  }
                                  value={
                                    tipo === CHANNEL_TYPES.IG_MESSENGER
                                      ? p.instagramId
                                      : p.pageId
                                  }
                                >
                                  {tipo === CHANNEL_TYPES.FB_MESSENGER
                                    ? `${p.pageName} (ID ${p.pageId})`
                                    : `${p.instagramUsername || "@"} — Página ${
                                        p.pageName
                                      } (IG ID ${p.instagramId})`}
                                </MenuItem>
                              ))}
                          </TextField>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              }

            // Para o canal de e-mail (SES), usamos uma UI especializada:
            // - input apenas para a parte antes do @
            // - sufixo @dominio mostrando workspace.xpro.me ou o domínio próprio
            // - domínio próprio opcional com UX guiada
            if (tipo === CHANNEL_TYPES.EMAIL_AWS_SES && (f.name === "email" || f.name === "email_domain")) {
              return null;
            }

            const isSecretCredentialField =
              f.name === "tokenwhatsapp" ||
              f.name === "token_messenger" ||
              f.name === "token_instagram" ||
              f.name === "apikeygupshup" ||
              f.name === "senha" ||
              f.name === "ecom_api_token";

            const hasStored = isSecretCredentialField && hasSecret(f.name);
            const isMasked = hasStored && !formData[f.name];

            const valueForField = isMasked ? "••••••••" : formData[f.name];

            const fieldWithHint =
              isSecretCredentialField && hasStored
                ? {
                    ...f,
                    required: f.required && !hasStored,
                    helperText:
                      f.helperText ||
                      (isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : "Um valor já está configurado. Preencha apenas se quiser trocar."),
                    readOnly: isMasked,
                    onClick: isMasked ? () => clearSecretField(f.name) : undefined,
                    onFocus: isMasked ? () => clearSecretField(f.name) : undefined,
                  }
                : f;

            return (
              <Grid key={f.name} item xs={12} sm={f.multiline ? 12 : 6}>
                <FieldControl
                  field={fieldWithHint}
                  value={valueForField}
                  onChange={onChange}
                />
              </Grid>
            );
            })}

            {isEmailSesChannel && (
              <>
                <Grid item xs={12} sm={6}>
                  {(() => {
                    const workspaceDomain = getWorkspaceDomain();
                    const emailValue = String(formData.email || "").trim();
                    const match = emailValue.match(/^([^@\s]+)@(.+)$/);
                    const localPart = match ? match[1] : "";
                    const domainFromEmail = match ? match[2].toLowerCase() : "";
                    const configuredDomain = String(formData.email_domain || "")
                      .trim()
                      .toLowerCase();
                    // Se houver domínio próprio configurado e ele não for um domínio compartilhado
                    // da Xpro, usamos esse domínio; caso contrário, usamos o domínio atual do e-mail
                    // (quando existir) ou o domínio do workspace.
                    const activeDomain =
                      configuredDomain && !isSharedXproDomain(configuredDomain)
                        ? configuredDomain
                        : domainFromEmail || workspaceDomain;

                    const handleLocalPartChange = (value) => {
                      onChange("email", value ? `${value}@${activeDomain}` : "");
                    };

                    return (
                      <TextField
                        label="Endereço de e-mail"
                        required
                        fullWidth
                        value={localPart}
                        onChange={(e) => handleLocalPartChange(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography variant="body2" color="text.secondary">
                                @{activeDomain}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          formData.email
                            ? `Usaremos ${formData.email} como remetente padrão deste canal.`
                            : "Informe apenas a parte antes do @ (ex.: contato, suporte). O domínio virá em seguida."
                        }
                      />
                    );
                  })()}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {!showCustomEmailDomain && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Usando o domínio padrão Xpro:{" "}
                          <strong>@{getWorkspaceDomain()}</strong>. Nenhuma configuração de DNS é necessária.
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowCustomEmailDomain(true)}
                          disabled={isLoading}
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Usar domínio próprio
                        </Button>
                      </>
                    )}

                    {showCustomEmailDomain && (
                      <>
                        <TextField
                          label="Domínio próprio (ex: suaempresa.com)"
                          fullWidth
                          value={formData.email_domain || ""}
                          onChange={(e) => {
                            const domain = e.target.value.trim();
                            onChange("email_domain", domain || "");

                            const emailValue = String(formData.email || "").trim();
                            const match = emailValue.match(/^([^@\s]+)@(.+)$/);
                            const localPart = match ? match[1] : "";
                            const activeDomain = domain || getWorkspaceDomain();

                            if (localPart) {
                              onChange("email", `${localPart}@${activeDomain}`);
                            }
                          }}
                          helperText="Após configurar o domínio, o endereço de e-mail acima deve usar exatamente este domínio."
                        />
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          onClick={() => {
                            setShowCustomEmailDomain(false);
                            onChange("email_domain", "");

                            const workspaceDomain = getWorkspaceDomain();
                            const emailValue = String(formData.email || "").trim();
                            const match = emailValue.match(/^([^@\s]+)@(.+)$/);
                            const localPart = match ? match[1] : "";
                            if (localPart) {
                              onChange("email", `${localPart}@${workspaceDomain}`);
                            }
                          }}
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Voltar para domínio padrão
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              </>
            )}

            {isWebsiteChannel && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Website do canal
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="URL temporária"
                    fullWidth
                    value={websiteConfig?.tempUrl || ""}
                    InputProps={{ readOnly: true }}
                    helperText="Esta URL é criada automaticamente para publicação imediata."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!isEditing || !channel?.id || websiteConfigLoading}
                    onClick={async () => {
                      try {
                        setWebsiteConfigMessage(null);
                        setWebsiteConfigLoading(true);
                        const res = await regenerateChannelWebsiteTempUrl(channel.id);
                        setWebsiteConfig(res || null);
                        setWebsiteConfigMessage("URL temporária regenerada.");
                      } catch (err) {
                        const msg = err?.message || "Não foi possível regenerar a URL temporária.";
                        setWebsiteConfigMessage(msg);
                      } finally {
                        setWebsiteConfigLoading(false);
                      }
                    }}
                  >
                    Regenerar URL
                  </Button>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Domínio customizado"
                    fullWidth
                    value={websiteConfig?.customDomain || ""}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        customDomain: e.target.value,
                      }))
                    }
                    placeholder="site.suaempresa.com"
                    helperText="Opcional. Configure DNS para publicar neste domínio."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Status do domínio"
                    fullWidth
                    value={websiteConfig?.domainStatus || "not_configured"}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Itens do menu"
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (Array.isArray(selected) ? selected.join(", ") : ""),
                    }}
                    value={websiteConfig?.menuItems || []}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        menuItems: Array.isArray(e.target.value) ? e.target.value : [],
                      }))
                    }
                    helperText="Selecione apenas páginas pré-definidas."
                  >
                    {[
                      "home",
                      "passagens",
                      "agencias",
                      "encomendas",
                      "contato",
                      "faq",
                      "sobre",
                      "beneficios",
                    ].map((item) => (
                      <MenuItem key={item} value={item}>
                        <Checkbox checked={Boolean((websiteConfig?.menuItems || []).includes(item))} />
                        <ListItemText primary={item} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Ordem do menu
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                      {(websiteConfig?.menuItems || []).map((item, index) => (
                        <Box
                          key={`order-${item}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="body2">{item}</Typography>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => moveWebsiteMenuItem(index, -1)}
                              disabled={index === 0}
                            >
                              <ArrowUpwardIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveWebsiteMenuItem(index, 1)}
                              disabled={index === (websiteConfig?.menuItems || []).length - 1}
                            >
                              <ArrowDownwardIcon fontSize="inherit" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Cor primária"
                    fullWidth
                    value={websiteConfig?.themeTokens?.primaryColor || ""}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        themeTokens: {
                          ...(prev?.themeTokens || {}),
                          primaryColor: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Cor secundária"
                    fullWidth
                    value={websiteConfig?.themeTokens?.secondaryColor || ""}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        themeTokens: {
                          ...(prev?.themeTokens || {}),
                          secondaryColor: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Cor de destaque"
                    fullWidth
                    value={websiteConfig?.themeTokens?.accentColor || ""}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        themeTokens: {
                          ...(prev?.themeTokens || {}),
                          accentColor: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Prompt de visual (IA)"
                    fullWidth
                    value={websiteThemePrompt}
                    onChange={(e) => setWebsiteThemePrompt(e.target.value)}
                    placeholder="Ex: visual moderno, fundo claro, botão laranja"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Imagem principal (hero) - URL"
                    fullWidth
                    value={websiteConfig?.mainImages?.heroImageSrc || ""}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        mainImages: {
                          ...(prev?.mainImages || {}),
                          heroImageSrc: e.target.value,
                        },
                      }))
                    }
                    helperText="Opcional. Se vazio, o site mostra uma imagem de marcação."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Alt do hero (opcional)"
                    fullWidth
                    value={websiteConfig?.mainImages?.heroImageAlt || ""}
                    onChange={(e) =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        mainImages: {
                          ...(prev?.mainImages || {}),
                          heroImageAlt: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Imagem banner secundário #1 - URL"
                    fullWidth
                    value={websiteConfig?.mainImages?.secondaryBannerImageSrcs?.[0] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setWebsiteConfig((prev) => {
                        const prevArr = prev?.mainImages?.secondaryBannerImageSrcs;
                        const arr = Array.isArray(prevArr) ? [...prevArr] : [];
                        arr[0] = v;
                        return {
                          ...(prev || {}),
                          mainImages: {
                            ...(prev?.mainImages || {}),
                            secondaryBannerImageSrcs: arr,
                          },
                        };
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Imagem banner secundário #2 - URL"
                    fullWidth
                    value={websiteConfig?.mainImages?.secondaryBannerImageSrcs?.[1] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setWebsiteConfig((prev) => {
                        const prevArr = prev?.mainImages?.secondaryBannerImageSrcs;
                        const arr = Array.isArray(prevArr) ? [...prevArr] : [];
                        arr[1] = v;
                        return {
                          ...(prev || {}),
                          mainImages: {
                            ...(prev?.mainImages || {}),
                            secondaryBannerImageSrcs: arr,
                          },
                        };
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Imagem banner secundário #3 - URL"
                    fullWidth
                    value={websiteConfig?.mainImages?.secondaryBannerImageSrcs?.[2] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setWebsiteConfig((prev) => {
                        const prevArr = prev?.mainImages?.secondaryBannerImageSrcs;
                        const arr = Array.isArray(prevArr) ? [...prevArr] : [];
                        arr[2] = v;
                        return {
                          ...(prev || {}),
                          mainImages: {
                            ...(prev?.mainImages || {}),
                            secondaryBannerImageSrcs: arr,
                          },
                        };
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!isEditing || !channel?.id || websiteConfigLoading}
                    onClick={async () => {
                      try {
                        setWebsiteConfigMessage(null);
                        setWebsiteConfigLoading(true);
                        const res = await suggestChannelWebsiteTheme(
                          channel.id,
                          websiteThemePrompt
                        );
                        const suggested = res?.suggestedTokens || {};
                        setWebsiteConfig((prev) => ({
                          ...(prev || {}),
                          themeTokens: {
                            ...(prev?.themeTokens || {}),
                            ...suggested,
                          },
                        }));
                        setWebsiteConfigMessage("Sugestão de tema aplicada ao formulário.");
                      } catch (err) {
                        const msg = err?.message || "Não foi possível gerar sugestão de tema.";
                        setWebsiteConfigMessage(msg);
                      } finally {
                        setWebsiteConfigLoading(false);
                      }
                    }}
                  >
                    Sugerir tema por IA
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="text"
                    startIcon={<RestartAltIcon />}
                    onClick={() =>
                      setWebsiteConfig((prev) => ({
                        ...(prev || {}),
                        themeTokens: {
                          primaryColor: "#0F4C81",
                          secondaryColor: "#F5A623",
                          accentColor: "#2ECC71",
                          textColor: "#1F2937",
                          backgroundColor: "#FFFFFF",
                          fontFamily: "Inter",
                          borderRadius: "8px",
                        },
                      }))
                    }
                  >
                    Restaurar tema padrão
                  </Button>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={!isEditing || !channel?.id || websiteConfigLoading}
                    onClick={async () => {
                      try {
                        setWebsiteConfigMessage(null);
                        setWebsiteConfigLoading(true);
                        const res = await setupChannelWebsiteDomain(channel.id);
                        setWebsiteDomainInfo(res || null);
                        setWebsiteConfigMessage("Configuração de domínio iniciada.");
                      } catch (err) {
                        const msg =
                          err?.message || "Não foi possível iniciar a configuração de domínio.";
                        setWebsiteConfigMessage(msg);
                      } finally {
                        setWebsiteConfigLoading(false);
                      }
                    }}
                  >
                    Configurar domínio
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="text"
                    disabled={!isEditing || !channel?.id || websiteConfigLoading}
                    onClick={async () => {
                      try {
                        setWebsiteConfigLoading(true);
                        const res = await getChannelWebsiteDomainStatus(channel.id);
                        setWebsiteDomainInfo(res || null);
                        setWebsiteConfigMessage("Status de DNS atualizado.");
                      } catch (err) {
                        const msg = err?.message || "Não foi possível verificar o DNS agora.";
                        setWebsiteConfigMessage(msg);
                      } finally {
                        setWebsiteConfigLoading(false);
                      }
                    }}
                  >
                    Verificar DNS
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={!isEditing || !channel?.id || websiteConfigLoading}
                    onClick={async () => {
                      try {
                        setWebsiteConfigMessage(null);
                        setWebsiteConfigLoading(true);
                        const res = await updateChannelWebsiteConfig(channel.id, {
                          customDomain: websiteConfig?.customDomain || "",
                          menuItems: websiteConfig?.menuItems || [],
                          themeTokens: websiteConfig?.themeTokens || {},
                          mainImages: websiteConfig?.mainImages || {},
                        });
                        setWebsiteConfig(res || null);
                        setWebsiteConfigMessage("Configurações do website salvas.");
                      } catch (err) {
                        const msg = err?.message || "Não foi possível salvar as configurações do website.";
                        setWebsiteConfigMessage(msg);
                      } finally {
                        setWebsiteConfigLoading(false);
                      }
                    }}
                  >
                    Salvar website
                  </Button>
                </Grid>
                {websiteDomainInfo?.expectedDns && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Status atual: <strong>{websiteDomainInfo.domainStatus || "pending_dns"}</strong>.{" "}
                      DNS esperado: {websiteDomainInfo.expectedDns.type}{" "}
                      <strong>{websiteDomainInfo.expectedDns.host}</strong> apontando para{" "}
                      <strong>{websiteDomainInfo.expectedDns.value}</strong>.
                    </Alert>
                  </Grid>
                )}
                {websiteDomainInfo?.fallbackUrl && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Enquanto o domínio não estiver ativo, use a URL temporária:{" "}
                      <strong>{websiteDomainInfo.fallbackUrl}</strong>
                    </Alert>
                  </Grid>
                )}
                {websiteConfigMessage && (
                  <Grid item xs={12}>
                    <Alert severity="info">{websiteConfigMessage}</Alert>
                  </Grid>
                )}
                {websiteAudit.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.25 }}>
                        Histórico de alterações do website
                      </Typography>
                      <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Ação"
                            value={websiteAuditActionFilter}
                            onChange={(e) => setWebsiteAuditActionFilter(e.target.value)}
                          >
                            <MenuItem value="all">Todas</MenuItem>
                            <MenuItem value="config_updated">Configuração atualizada</MenuItem>
                            <MenuItem value="temp_url_regenerated">URL temporária regenerada</MenuItem>
                            <MenuItem value="domain_setup_started">Configuração de domínio iniciada</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Período"
                            value={websiteAuditPeriodFilter}
                            onChange={(e) => setWebsiteAuditPeriodFilter(e.target.value)}
                          >
                            <MenuItem value="24h">Últimas 24 horas</MenuItem>
                            <MenuItem value="7d">Últimos 7 dias</MenuItem>
                            <MenuItem value="30d">Últimos 30 dias</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                      <Stack spacing={0.75}>
                        {filteredWebsiteAudit.slice(0, 12).map((entry) => (
                          <Box
                            key={`audit-${entry.id}`}
                            sx={{
                              display: "grid",
                              gap: 1,
                              borderBottom: "1px dashed",
                              borderColor: "divider",
                              pb: 0.5,
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                              <Typography variant="caption">
                                {formatAuditAction(entry.action)}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {entry.createdAt}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleWebsiteAuditExpanded(entry.id)}
                                  sx={{ p: 0.25 }}
                                >
                                  {expandedWebsiteAuditIds.includes(Number(entry.id)) ? (
                                    <ExpandLessIcon fontSize="inherit" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="inherit" />
                                  )}
                                </IconButton>
                              </Box>
                            </Box>
                            {buildAuditDiffSummary(entry) && (
                              <Typography variant="caption" color="text.secondary">
                                {buildAuditDiffSummary(entry)}
                              </Typography>
                            )}
                            {expandedWebsiteAuditIds.includes(Number(entry.id)) && (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: "action.hover",
                                  overflowX: "auto",
                                }}
                              >
                                <Typography
                                  component="pre"
                                  variant="caption"
                                  sx={{
                                    m: 0,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {JSON.stringify(entry?.payload || {}, null, 2)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                        {filteredWebsiteAudit.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Nenhum evento no período/filtro selecionado.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: websitePreviewTheme.backgroundColor,
                      color: websitePreviewTheme.textColor,
                      fontFamily: websitePreviewTheme.fontFamily,
                      borderColor: websitePreviewTheme.secondaryColor,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: websitePreviewTheme.borderRadius,
                        background: websitePreviewTheme.primaryColor,
                        color: "#fff",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "inherit" }}>
                        Pré-visualização do Website
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        {websitePreviewMenu.map((item) => (
                          <Chip
                            key={`preview-${item}`}
                            size="small"
                            label={String(item).toUpperCase()}
                            sx={{
                              bgcolor: "rgba(255,255,255,0.16)",
                              color: "#fff",
                              borderRadius: websitePreviewTheme.borderRadius,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={8}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: websitePreviewTheme.borderRadius,
                            border: "1px solid",
                            borderColor: websitePreviewTheme.secondaryColor,
                          }}
                        >
                          <Typography variant="h6" sx={{ color: websitePreviewTheme.primaryColor }}>
                            {formData?.nome || "Nome da Viação"}
                          </Typography>
                          <Typography variant="body2">
                            Exemplo de hero do site com os estilos selecionados.
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              mt: 1.5,
                              bgcolor: websitePreviewTheme.accentColor,
                              borderRadius: websitePreviewTheme.borderRadius,
                            }}
                          >
                            Comprar Passagem
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: websitePreviewTheme.borderRadius,
                            border: "1px dashed",
                            borderColor: websitePreviewTheme.secondaryColor,
                            height: "100%",
                          }}
                        >
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Tokens ativos</strong>
                          </Typography>
                          <Typography variant="caption" display="block">
                            Primária: {websitePreviewTheme.primaryColor}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Secundária: {websitePreviewTheme.secondaryColor}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Destaque: {websitePreviewTheme.accentColor}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}

            {hasCustomEmailDomain && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Status da integração de domínio
                </Typography>

                {!isEditing && (
                  <Alert severity="info">
                    Após salvar o canal, vamos criar (ou atualizar) a identidade de e-mail na AWS
                    com base no domínio informado. Se você usar um domínio próprio, mostraremos aqui
                    os registros DNS que precisam ser configurados para concluir a verificação.
                  </Alert>
                )}

                {isEditing && (
                  <Box sx={{ mt: 1 }}>
                    {emailDomainLoading && (
                      <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                    )}
                    {!emailDomainLoading && emailDomainError && (
                      <Alert severity="error">{emailDomainError}</Alert>
                    )}
                    {!emailDomainLoading && !emailDomainError && emailDomainStatus && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "background.default",
                          mb: 1.5,
                        }}
                      >
                        {/* Resumo em uma linha: domínio + status geral */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1}
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {emailDomainStatus.usingDefaultDomain
                              ? `Domínio Xpro: ${emailDomainStatus.domain}`
                              : emailDomainStatus.domain}
                          </Typography>
                          {!emailDomainStatus.usingDefaultDomain && (
                            <Chip
                              size="small"
                              label="domínio próprio"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.75rem" }}
                            />
                          )}
                        </Stack>

                        {/* 3 passos em lista compacta (MX como subitem do passo 2) */}
                        <List dense disablePadding sx={{ mb: 2 }}>
                          <ListItem alignItems="flex-start" disablePadding sx={{ py: 0.25 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {String(emailDomainStatus.ses?.verificationStatus).toUpperCase() === "SUCCESS" ? (
                                <CheckIcon fontSize="small" color="success" />
                              ) : (
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid", borderColor: "warning.main" }} />
                              )}
                              <Typography variant="body2">
                                <strong>Passo 1.</strong> Domínio na AWS (SES){" "}
                                {String(emailDomainStatus.ses?.verificationStatus).toUpperCase() === "SUCCESS" ? "— verificado" : "— aguardando"}
                              </Typography>
                            </Stack>
                          </ListItem>
                          <ListItem alignItems="flex-start" disablePadding sx={{ py: 0.25 }}>
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                              {emailDomainStatus.inbound?.bucketConfigured && emailDomainStatus.inbound?.lambdaConfigured &&
                               (!emailDomainStatus.mxCheck || emailDomainStatus.mxCheck.ok) ? (
                                <CheckIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
                              ) : (
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid", borderColor: "warning.main", mt: 0.25 }} />
                              )}
                              <Box>
                                <Typography variant="body2">
                                  <strong>Passo 2.</strong> Recebimento na infraestrutura Xpro
                                  {emailDomainStatus.inbound?.bucketConfigured && emailDomainStatus.inbound?.lambdaConfigured ? " — OK" : " — pendente"}
                                </Typography>
                                {emailDomainStatus.mxCheck != null && !emailDomainStatus.usingDefaultDomain && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 2, mt: 0.25 }}>
                                    MX {emailDomainStatus.mxCheck.ok ? "apontando para SES" : `deve apontar para: ${emailDomainStatus.mxCheck.expectedHost}`}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </ListItem>
                          <ListItem alignItems="flex-start" disablePadding sx={{ py: 0.25 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid", borderColor: "divider" }} />
                              <Typography variant="body2" color="text.secondary">
                                <strong>Passo 3.</strong> Teste de envio/recebimento (opcional)
                              </Typography>
                            </Stack>
                          </ListItem>
                        </List>

                        {/* Ações em uma única barra */}
                        {!emailDomainStatus.usingDefaultDomain && (
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={refreshEmailDomainStatus}
                              disabled={emailDomainLoading}
                            >
                              {emailDomainLoading ? "Atualizando…" : "Atualizar status"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={isSendingTestEmail || !formData.email}
                              onClick={async () => {
                                if (!channel?.id) return;
                                setTestEmailMessage(null);
                                setIsSendingTestEmail(true);
                                try {
                                  await sendChannelEmailDomainTest(channel.id);
                                  setTestEmailMessage(
                                    "E-mail de teste enviado. Verifique a caixa de entrada ou a plataforma em alguns minutos."
                                  );
                                } catch (err) {
                                  // eslint-disable-next-line no-console
                                  console.error("Erro ao enviar e-mail de teste:", err);
                                  setTestEmailMessage(
                                    "Não foi possível enviar o e-mail de teste. Verifique o endereço do canal e tente novamente."
                                  );
                                } finally {
                                  setIsSendingTestEmail(false);
                                }
                              }}
                            >
                              {isSendingTestEmail ? "Enviando…" : "Enviar e-mail de teste"}
                            </Button>
                          </Stack>
                        )}

                        {testEmailMessage && (
                          <Alert severity="info" sx={{ mb: 2 }}>{testEmailMessage}</Alert>
                        )}

                        {/* MX pendente: alerta curto */}
                        {!emailDomainStatus.usingDefaultDomain &&
                          emailDomainStatus.mxCheck &&
                          !emailDomainStatus.mxCheck.ok && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              Configure o registro <strong>MX</strong> no DNS para{" "}
                              <strong>{emailDomainStatus.mxCheck.expectedHost}</strong>
                              {Array.isArray(emailDomainStatus.mxCheck.currentHosts) &&
                                emailDomainStatus.mxCheck.currentHosts.length > 0 && (
                                  <> (atual: {emailDomainStatus.mxCheck.currentHosts.join(", ")})</>
                                )}
                              . Sem isso, e-mails podem devolver erro 550.
                            </Alert>
                          )}

                        {/* Domínio Xpro: mensagem única */}
                        {emailDomainStatus.usingDefaultDomain && (
                          <Alert severity="success" sx={{ mb: 2 }}>
                            Domínio gerenciado pela Xpro. Nenhuma configuração DNS necessária.
                          </Alert>
                        )}

                        {/* Domínio próprio: mensagem curta + link para DNS */}
                        {!emailDomainStatus.usingDefaultDomain && (
                          <Box sx={{ mt: 1 }}>
                            {String(emailDomainStatus.ses?.verificationStatus).toUpperCase() === "SUCCESS" ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Domínio verificado na AWS. Os registros abaixo são para conferência ou para recriar em outro DNS.
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Crie os registros DNS abaixo no seu provedor (Registro.br, Cloudflare, etc.). Após a propagação, o status ficará como verificado.
                              </Typography>
                            )}

                            <Button
                              size="small"
                              variant="text"
                              sx={{ mt: 0.5, mb: 1 }}
                              onClick={() => setShowDnsDetails((v) => !v)}
                            >
                              {showDnsDetails ? "Ocultar detalhes de DNS" : "Mostrar detalhes de DNS"}
                            </Button>

                            {showDnsDetails && (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Nome (Host)</TableCell>
                                    <TableCell>Valor (Destino)</TableCell>
                                    <TableCell align="right">TTL</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(emailDomainStatus.dnsRecords || []).map((r, idx) => (
                                    <TableRow key={`${r.Type}-${r.Name}-${idx}`}>
                                      <TableCell>{r.Type}</TableCell>
                                      <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                            {r.Name}
                                          </Typography>
                                          <Tooltip title="Copiar nome (Host)">
                                            <IconButton
                                              size="small"
                                              aria-label="Copiar nome do registro"
                                              onClick={async () => {
                                                try {
                                                  await navigator.clipboard.writeText(r.Name);
                                                } catch {
                                                  setClientSideError("Não foi possível copiar. Copie manualmente.");
                                                }
                                              }}
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      </TableCell>
                                      <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                            {r.Value}
                                          </Typography>
                                          <Tooltip title="Copiar valor (Destino)">
                                            <IconButton
                                              size="small"
                                              aria-label="Copiar valor do registro"
                                              onClick={async () => {
                                                try {
                                                  await navigator.clipboard.writeText(r.Value);
                                                } catch {
                                                  setClientSideError("Não foi possível copiar. Copie manualmente.");
                                                }
                                              }}
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right">{r.TTL}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        )}
                      </Paper>
                    )}
                  </Box>
                )}
              </Grid>
            )}

            {/* Ações especiais para WhatsApp Gupshup (ao final do formulário) */}
            {tipo === CHANNEL_TYPES.WHATSAPP_GUPSHUP && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Você pode sincronizar automaticamente os templates aprovados na Gupshup
                  para a tabela de templates deste canal. Use esta opção quando criar ou
                  alterar templates no painel da Gupshup.
                </Alert>
                <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleImportGupshupTemplatesClick}
                    disabled={isImportingTemplates || isLoading}
                  >
                    {isImportingTemplates ? "Importando templates..." : "Importar templates da Gupshup"}
                  </Button>
                </Stack>
                {importTemplatesError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {importTemplatesError}
                  </Alert>
                )}
                {importTemplatesResult && (
                  <Alert
                    severity={importTemplatesResult.warning ? "info" : "success"}
                    sx={{ mb: 1 }}
                  >
                    {importTemplatesResult.warning
                      ? importTemplatesResult.warning
                      : `Templates sincronizados: ${
                          importTemplatesResult.imported ?? 0
                        } novos, ${importTemplatesResult.updated ?? 0} atualizados. Total retornado pela Gupshup: ${
                          importTemplatesResult.totalRemote ?? 0
                        }.`}
                  </Alert>
                )}
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                      Templates deste canal
                    </Typography>
                    {!channelTemplatesLoading && channelTemplates.length > 0 && (
                      <Chip
                        size="small"
                        label={`${channelTemplates.length} template${channelTemplates.length !== 1 ? "s" : ""}`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  {channelTemplatesLoading ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton
                          key={i}
                          variant="rounded"
                          height={52}
                          sx={{ mb: i < 5 ? 1 : 0, borderRadius: 1 }}
                        />
                      ))}
                    </Paper>
                  ) : channelTemplates.length === 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        textAlign: "center",
                        bgcolor: "action.hover",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Nenhum template neste canal
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use o botão &quot;Importar templates da Gupshup&quot; acima ou salve o canal primeiro.
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        maxHeight: 360,
                        overflow: "auto",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <List disablePadding>
                        {channelTemplates.map((tpl, idx) => (
                          <ListItem
                            key={tpl.id}
                            divider={idx < channelTemplates.length - 1}
                            sx={{
                              py: 1.5,
                              px: 2,
                              bgcolor: idx % 2 === 0 ? "transparent" : "action.hover",
                              "&:hover": { bgcolor: "action.selected" },
                              alignItems: "flex-start",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                  <Typography variant="body2" fontWeight={600} component="span">
                                    {tpl.name || `Template #${tpl.id}`}
                                  </Typography>
                                  {tpl.languageCode && (
                                    <Chip
                                      size="small"
                                      label={tpl.languageCode}
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: "0.7rem" }}
                                    />
                                  )}
                                  {tpl.status != null && String(tpl.status).toLowerCase() === "approved" && (
                                    <Chip
                                      size="small"
                                      label="Aprovado"
                                      color="success"
                                      sx={{ height: 20, fontSize: "0.7rem" }}
                                    />
                                  )}
                                </Stack>
                              }
                              secondary={
                                tpl.preview ? (
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      color: "text.secondary",
                                      lineHeight: 1.5,
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {tpl.preview}
                                  </Typography>
                                ) : null
                              }
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            {!isEditing && (
              <Button variant="outlined" onClick={() => setStep(1)} disabled={isLoading}>
                Voltar
              </Button>
            )}
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !allRequiredFilled}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
